import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewInit,
  ChangeDetectorRef,
  Inject
} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { UntypedFormGroup, UntypedFormBuilder, Validators, FormControl } from '@angular/forms';
import { Observable, Subscription, Subject } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatTabGroup } from '@angular/material/tabs';
import { takeUntil } from 'rxjs/operators';

import { TranslateService } from '@ngx-translate/core';
import { StandortService, Standort } from '../services/standort.service';
import { ObjektStrukturService, ObjektStrukturNode } from '../services/objekt-struktur.service';
import { KundeService, Kunde } from '../services/kunde.service';
import { MitarbeiterService, TechnicianOption } from '../services/mitarbeiter.service';
import { KontaktService, Kontakt } from '../services/kontakt.service';
import { RechnungsvertragService, Rechnungsvertrag } from '../services/rechnungsvertrag.service';
import { JournalService } from '../@core/journal.service';
import { ConfirmDialogComponent } from '../@shared/dialog/confirm-dialog/confirm-dialog.component';
import { ProjektaufgabeComponent } from '../projektaufgabe/projektaufgabe.component';
import { LabelPreviewDialogComponent } from './label-preview-dialog/label-preview-dialog.component';
import { ReportService, ReportCategory, ReportCategoryOption } from '../services/report.service';
import { TrendChartService } from '../services/trend-chart.service';
import { PlanService } from '../services/plan.service';
import { environment } from '../../environments/environment';
import { HostListener } from '@angular/core';

interface RoomIndicator {
  id: string;
  name: string;
  current_value: string;
  einheit: string;
  schwellenwert: string;
  mehr_weniger: number;
  indikator_number?: number;
  status?: number | boolean;
  room?: {
    status?: number | boolean;
    sektion?: {
      status?: number | boolean;
      gebaeude?: {
        status?: number | boolean;
        name: string;
      };
      name: string;
    };
  };
}

interface ReportContact {
  email: string;
  name: string;
  anrede: string;
}

// Inline progress dialog component
@Component({
  selector: 'app-progress-dialog-inline',
  template: `
    <div class="progress-dialog-container">
      <h2 mat-dialog-title>{{ data.title || 'Bitte warten' }}</h2>
      <mat-dialog-content>
        <div class="flex flex-col items-center justify-center py-6">
          <mat-spinner diameter="50"></mat-spinner>
          <p class="mt-4 text-gray-600 text-center font-medium">{{ data.message || 'Verarbeitung läuft...' }}</p>
          <p class="mt-2 text-sm text-gray-500 text-center max-w-md">{{ data.detailMessage || '' }}</p>
        </div>
      </mat-dialog-content>
      <mat-dialog-actions align="end">
        <button mat-button (click)="onCancel()" [disabled]="data.cancelling">
          <span *ngIf="!data.cancelling">Abbrechen</span>
          <span *ngIf="data.cancelling">Wird abgebrochen...</span>
        </button>
      </mat-dialog-actions>
    </div>
  `,
  styles: [`
    .progress-dialog-container {
      min-width: 400px;
    }
    mat-spinner {
      margin: 0 auto;
    }
    mat-dialog-content {
      padding: 0 24px;
    }
    mat-dialog-actions {
      padding: 16px 24px;
    }
  `],
  standalone: false
})
export class ProgressDialogComponent {
  constructor(
    public dialogRef: MatDialogRef<ProgressDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { 
      title?: string; 
      message?: string;
      detailMessage?: string;
      showCancel?: boolean;
      cancelling?: boolean;
    }
  ) {}

  onCancel(): void {
    this.data.cancelling = true;
    this.dialogRef.close('cancel');
  }
}

// --- Trend Preview Dialog Component ---
@Component({
  selector: 'app-trend-preview-dialog',
  template: `
    <div class="trend-preview-dialog">
      <div mat-dialog-title class="flex items-center justify-between">
        <span>{{ data.title }}</span>
        <button mat-icon-button (click)="dialogRef.close()">
          <mat-icon>close</mat-icon>
        </button>
      </div>
      <mat-dialog-content>
        <iframe
          #previewFrame
          style="width: 100%; height: calc(85vh - 120px); border: 1px solid #ddd; background: white;">
        </iframe>
      </mat-dialog-content>
      <mat-dialog-actions align="end">
        <button mat-button (click)="dialogRef.close()">Schließen</button>
        <button mat-raised-button color="primary" (click)="onSaveAsPdf()" [disabled]="isSaving">
          <mat-icon class="mr-1">picture_as_pdf</mat-icon>
          <span *ngIf="isSaving">PDF wird erstellt...</span>
          <span *ngIf="!isSaving">Als PDF speichern</span>
        </button>
      </mat-dialog-actions>
    </div>
  `,
  styles: [`
    .trend-preview-dialog {
      min-width: 90vw;
      max-width: 95vw;
    }
    mat-dialog-content {
      max-height: 85vh !important;
      padding: 0 !important;
      overflow: hidden;
    }
    mat-dialog-actions {
      padding: 12px 24px;
      border-top: 1px solid #e0e0e0;
    }
    [mat-dialog-title] {
      margin: 0;
      padding: 12px 24px;
      border-bottom: 1px solid #e0e0e0;
      font-size: 16px;
      font-weight: 500;
    }
  `],
  standalone: false
})
export class TrendPreviewDialogComponent implements AfterViewInit {
  @ViewChild('previewFrame') previewFrame!: ElementRef<HTMLIFrameElement>;
  isSaving = false;

  constructor(
    public dialogRef: MatDialogRef<TrendPreviewDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      title: string;
      htmlContent: string;
      chartImages: { [key: string]: string };
      reportType: string;
      dateFrom: string;
      dateTo: string;
    }
  ) {}

  ngAfterViewInit(): void {
    // Write HTML directly to iframe to bypass Angular sanitization
    setTimeout(() => {
      const iframe = this.previewFrame?.nativeElement as HTMLIFrameElement;
      if (iframe) {
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (doc) {
          doc.open();
          doc.write(this.data.htmlContent);
          doc.close();
        }
      }
    }, 100);
  }

  onSaveAsPdf(): void {
    this.isSaving = true;
    this.dialogRef.close('save_pdf');
  }
}

@Component({
  selector: 'app-standort-edit',
  templateUrl: './standort_edit.component.html',
  styleUrls: ['./standort_edit.component.scss']
})
export class StandortEditComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(MatTabGroup) tabGroup!: MatTabGroup;
  
  standortForm: UntypedFormGroup;
  standort: Standort | null = null;
  isLoading = false;
  isSaving = false;

  // Billing (Rechnungsvertrag) for the Abrechnung tab
  rechnungsvertrag: Rechnungsvertrag | null = null;
  billingLoading = false;
  billingSaving = false;
  billingEdit = false;
  
  // Report contacts management
  reportContacts: ReportContact[] = [];
  newContactEmail: string = '';
  newContactName: string = '';
  newContactAnrede: string = '';
  roomIndicators?: RoomIndicator[];
  filteredRoomIndicators: RoomIndicator[] = [];
  isEditMode = false;
  isNewStandort = false;
  private subscription: Subscription;
  standortId: string | null = null;
  
  
  // Client management
  clients: Kunde[] = [];
  selectedClient: Kunde | null = null;
  kundeId: string | null = null;
  clientSearchControl = new FormControl();
  filteredClients: Kunde[] = [];

  // Techniker management
  availableTechnicians: TechnicianOption[] = [];
  isLoadingTechnicians = false;

  // Contact management
  availableContacts: Kontakt[] = [];
  isLoadingContacts = false;


  // Objekt-Struktur data
  objektStrukturData: ObjektStrukturNode[] = [];
  objektStrukturLoading = false;

  // Environment for template access
  environment = environment;

  // Status filter (Aktiv/Inaktiv) like client component
  statusFilterOptions = [
    { id: 1, label: 'Aktiv' },
    { id: 0, label: 'Inaktiv' },
    { id: -1, label: 'Alle' }
  ];
  selectedStatusFilter = 1; // default show only active
  isStatusMenuOpen = false;

  // Report generation
  selectedReportCategory: ReportCategory = 'systemplan';
  availableReportCategories: ReportCategoryOption[] = [];
  isCategoryDropdownOpen = false;
  isGeneratingReport = false;
  isExportingPDF = false;
  private reportGenerationSubscription?: Subscription;

  // Trend analysis report settings
  trendReportType: string = 'auswertung_trend';
  trendSelectedYear: number = new Date().getFullYear();
  trendYearOptions: number[] = (() => {
    const currentYear = new Date().getFullYear();
    const years: number[] = [];
    for (let y = 2025; y <= currentYear; y++) {
      years.push(y);
    }
    return years;
  })();
  trendDateFrom: string = new Date().getFullYear() + '-01-01';
  trendDateTo: string = new Date().getFullYear() + '-12-31';
  trendReportTypes = [
    { value: 'auswertung_trend', label: 'Auswertung Trend' },
    { value: 'trend_matrix', label: 'Auswertung Trend: Matrix' },
    { value: 'periodenbericht', label: 'Periodenbericht' }
  ];

  // Getter methods for template access
  get standorteStatusOptions() {
    return (environment as any).standorte_status || [];
  }

  get berichtSpracheOptions() {
    return (environment as any).bericht_sprache || [];
  }

  get berichtVersandOptions() {
    return (environment as any).bericht_versand || [];
  }

  /**
   * Get the effective date for "Datum ab wann"
   * If datum_ab_wann is set, use it; otherwise use the created date
   */
  getEffectiveDatumAbWann(): string {
    if (this.standort?.datum_ab_wann) {
      // Convert to German format (DD.MM.YYYY)
      return this.formatDateToGerman(this.standort.datum_ab_wann);
    }
    
    // If no datum_ab_wann is set, use created_at date
    if (this.standort?.created_at) {
      // Convert created_at to German format
      return this.formatDateToGerman(this.standort.created_at);
    }
    
    // Fallback to current date in German format
    return this.formatDateToGerman(new Date().toISOString());
  }

  /**
   * Format date to German format (DD.MM.YYYY)
   */
  private formatDateToGerman(dateString: string): string {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}.${month}.${year}`;
  }

  /**
   * Convert any date format to ISO format (YYYY-MM-DD) for date input fields
   */
  private convertToIsoDate(dateString: string | null | undefined): string {
    if (!dateString) return '';
    
    // Already in ISO format (YYYY-MM-DD)
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateString;
    }
    
    // Handle datetime strings (YYYY-MM-DD HH:MM:SS or YYYY-MM-DDTHH:MM:SS)
    if (dateString.match(/^\d{4}-\d{2}-\d{2}[\sT]/)) {
      return dateString.split(/[\sT]/)[0];
    }
    
    // Handle German format (DD.MM.YYYY)
    if (dateString.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
      const parts = dateString.split('.');
      if (parts.length === 3) {
        const day = parts[0].padStart(2, '0');
        const month = parts[1].padStart(2, '0');
        const year = parts[2];
        return `${year}-${month}-${day}`;
      }
    }
    
    // Try to parse as Date and convert to ISO
    try {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
    } catch (e) {
      // Invalid date
    }
    
    return '';
  }





  private destroy$ = new Subject<void>();

  constructor(
    private standortService: StandortService,
    private objektStrukturService: ObjektStrukturService,
    private kundeService: KundeService,
    private mitarbeiterService: MitarbeiterService,
    private kontaktService: KontaktService,
    private rechnungsvertragService: RechnungsvertragService,
    private journalService: JournalService,
    private reportService: ReportService,
    private planService: PlanService,
    private trendChartService: TrendChartService,
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private changeDetectorRef: ChangeDetectorRef,
    private translate: TranslateService
  ) {
    console.log('🏗️ StandortEditComponent constructor called');
    this.standortForm = this.createForm();
    console.log('📝 Form created successfully');
  }

  ngOnInit() {
    console.log('🏢 StandortEditComponent ngOnInit - Component loaded successfully');
    console.log('📍 Current URL:', window.location.href);
    console.log('🎯 Component should be loaded within shell');
    console.log('🔍 Route params:', this.route.snapshot.params);
    console.log('🔍 Route data:', this.route.snapshot.data);
    console.log('🔍 Route URL:', this.route.snapshot.url);
    
    // Check if this is the "new" route by looking at the URL segments
    const urlSegments = this.route.snapshot.url;
    const isNewRoute = urlSegments.length > 0 && urlSegments[urlSegments.length - 1].path === 'new';
    
    console.log('🔍 URL segments:', urlSegments);
    console.log('🔍 Is new route:', isNewRoute);
    
    if (isNewRoute) {
      console.log('🆕 This is the new standort route');
      this.standortId = null;
      this.isNewStandort = true;
      
      // Get kundeId from query parameters if coming from client-edit
      this.route.queryParams.subscribe(params => {
        this.kundeId = params['kundeId'];
        console.log('🔍 KundeId from query params:', this.kundeId);
        
        // Refresh flag is no longer needed since active projektaufgabe is shown in projektaufgabe-edit
        
        if (this.kundeId) {
          // Check if this is a kontakt UUID (coming from kontakt-edit) or client UUID (coming from client-edit)
          // If coming from kontakt-edit, we need to load the client through the kontakt relationship
          this.loadClientFromKontaktOrDirect(this.kundeId);
        } else {
          // Load all clients for dropdown selection
          this.loadClients();
        }
      });
      
      this.enterEditMode();
      
      // Set up autocomplete functionality
      this.setupClientAutocomplete();
    } else {
      // Get route parameters for existing standort
      this.route.params.subscribe(params => {
        console.log('📋 Route params received:', params);
        this.standortId = params['id'];
        this.isNewStandort = false;
        console.log('🆔 Standort ID:', this.standortId);
        console.log('🆕 Is new standort:', this.isNewStandort);
        
        if (this.standortId) {
          console.log('📋 Loading existing standort with ID:', this.standortId);
          this.loadStandort(this.standortId);
          // For existing standorte, start in view mode (not edit mode)
          this.isEditMode = false;
          
          // Set up autocomplete functionality for existing standorte too
          this.setupClientAutocomplete();
        }
      });
      
      // Refresh flag is no longer needed since active projektaufgabe is shown in projektaufgabe-edit
    }
    
    // Initialize report categories (struktur categories + trend analyse)
    this.availableReportCategories = this.reportService.getAvailableReportCategories()
      .filter(cat => ['systemplan', 'qr_etiketten', 'indikatoren_liste', 'trend_analyse'].includes(cat.value));
    
  }

  ngOnDestroy(): void {
    // Cancel any ongoing report generation
    if (this.reportGenerationSubscription) {
      this.reportGenerationSubscription.unsubscribe();
      this.reportGenerationSubscription = undefined;
    }
    
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    // Handle tab query parameter
    this.route.queryParams.subscribe(params => {
      if (params['tab'] && this.tabGroup) {
        const tabName = params['tab'].toLowerCase();
        const tabIndex = this.getTabIndexByName(tabName);
        if (tabIndex !== -1) {
          setTimeout(() => {
            this.tabGroup.selectedIndex = tabIndex;
          }, 100);
        }
      }
    });
  }

  /**
   * Map tab name to tab index
   */
  private getTabIndexByName(tabName: string): number {
    const tabMap: { [key: string]: number } = {
      'information': 0,
      'standort-information': 0,
      'objekt-struktur': 1,
      'objektstruktur': 1,
      'dokumente': 2,
      'plaene': 3,
      'pläne': 3,
      'plane': 3,
      'indikatoren': 4,
      'projekt': 5,
      'projektaufgabe': 5,
      'projektaufgaben': 5
    };
    return tabMap[tabName] ?? -1;
  }

  loadClients() {
    console.log('📋 Loading all active clients for dropdown');
    // In edit mode we must include inactive Kunden so existing relationships can still be displayed/saved.
    const includeInactive = this.isEditMode === true;
    this.kundeService.getActiveKundenForDropdown(includeInactive).subscribe({
      next: (response) => {
        this.clients = response.data || [];
        this.filteredClients = this.clients;
        console.log('✅ Loaded active clients:', this.clients.length);

        // Reconcile current form value with loaded clients
        const currentUuid: string = this.standortForm.get('kunde_id')?.value;
        if (currentUuid) {
          const matched = this.clients.find(c => c.uuid === currentUuid);
          if (matched) {
            this.selectedClient = matched;
            this.kundeId = matched.uuid;
            // Set the autocomplete input value to display the client name
            if (this.isEditMode || this.isNewStandort) {
              this.clientSearchControl.setValue(matched, { emitEvent: false });
            }
            console.log('🔗 Reconciled client from form UUID:', matched.suchbegriff);
          } else {
            // Value is not a client UUID → clear and require selection
            this.selectedClient = null;
            this.kundeId = null;
            this.standortForm.patchValue({ kunde_id: '' });
            if (this.isEditMode || this.isNewStandort) {
              this.clientSearchControl.setValue('', { emitEvent: false });
            }
            console.warn('⚠️ Form held a non-client UUID. Cleared. Please select a Kunde.');
          }
        } else if (this.selectedClient && (this.isEditMode || this.isNewStandort)) {
          // If we have a selectedClient but no form value, set the autocomplete input
          this.clientSearchControl.setValue(this.selectedClient, { emitEvent: false });
        }
      },
      error: (error) => {
        console.error('❌ Error loading clients:', error);
        this.snackBar.open(this.translate.instant('STANDORT_EDIT.ERROR_LOAD_CLIENTS'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      }
    });
  }

  loadClient(clientId: string) {
    console.log('📋 Loading specific client:', clientId);
    
    // Since we're now using UUIDs consistently, use getKundeByUuid
    this.kundeService.getKundeByUuid(clientId).subscribe({
      next: (response) => {
        this.selectedClient = response.data;
        this.kundeId = response.data.uuid; // Store the UUID
        console.log('✅ Loaded client via UUID:', this.selectedClient?.suchbegriff);
        
        // Load contacts for the client
        this.loadContacts();
      },
      error: (error) => {
        console.error('❌ Error loading client via UUID:', error);
        this.snackBar.open(this.translate.instant('STANDORT_EDIT.ERROR_LOAD_CLIENT'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      }
    });
  }

  loadClientFromKontaktOrDirect(uuid: string) {
    console.log('🔍 Loading client from UUID (could be client or kontakt):', uuid);
    
    // First try to load as a client directly
    this.kundeService.getKundeByUuid(uuid).subscribe({
      next: (response) => {
        // Success - this was a client UUID
        this.selectedClient = response.data;
        this.kundeId = response.data.uuid;
        console.log('✅ Loaded client directly via UUID:', this.selectedClient?.suchbegriff);
        
        // Store the client UUID in the form
        this.standortForm.patchValue({ 
          kunde_id: response.data.uuid
        });
      },
      error: (error) => {
        console.log('❌ Not a client UUID, trying as kontakt UUID...');
        // If that fails, try to load as a kontakt and get the client from there
        this.loadClientFromKontakt(uuid);
      }
    });
  }

  setupClientAutocomplete() {
    // Initialize filtered clients with all clients
    this.filteredClients = this.clients;
    
    // Set up autocomplete for client selection
    this.clientSearchControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (typeof value === 'object' && value !== null) {
          // A client object was selected
          console.log('🔄 Client selected from autocomplete:', value);
          console.log('🔄 Client UUID from autocomplete:', value.uuid);
          console.log('🔄 Client ID from autocomplete:', value.id);
          this.selectedClient = value;
          this.kundeId = value.uuid; // Use UUID, not integer ID
          // Store the client UUID directly in the form
          this.standortForm.patchValue({ 
            kunde_id: value.uuid // Use UUID, not integer ID
          });
          console.log('✅ Form updated from autocomplete with client UUID:', value.uuid);
          console.log('✅ Form value after autocomplete update:', this.standortForm.value);
          
          // Load contacts for the selected client
          this.loadContacts();
        } else if (typeof value === 'string') {
          // User is typing, filter clients
          this.filterClients(value);
        }
      });
  }


  filterClients(searchTerm: string) {
    if (!searchTerm || searchTerm.length < 2) {
      this.filteredClients = this.clients;
      return;
    }
    
    // Filter clients based on search term
    this.filteredClients = this.clients.filter(client => 
      client.suchbegriff.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.suchbegriff.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    console.log('🔍 Filtered clients:', this.filteredClients.length);
  }

  onClientSelectionChange(event: any) {
    const client = event.value;
    console.log('🔄 Client selection changed:', client);
    console.log('🔄 Client UUID:', client?.uuid);
    console.log('🔄 Client ID:', client?.id);
    
    if (client && typeof client === 'object') {
      this.selectedClient = client;
      this.kundeId = client.uuid; // Use UUID, not integer ID
      // Store the client UUID directly in kunde_id
      this.standortForm.patchValue({ 
        kunde_id: client.uuid // Use UUID, not integer ID
      });
      console.log('✅ Form updated with client UUID:', client.uuid);
      console.log('✅ Form value after update:', this.standortForm.value);
    } else {
      this.selectedClient = null;
      this.kundeId = null;
      this.standortForm.patchValue({ 
        kunde_id: ''
      });
    }
  }

  // setPrimaryContactForKunde method removed - we now store client UUID directly

  displayClientName(client: Kunde): string {
    return client ? client.suchbegriff : '';
  }

  loadClientFromKontakt(kontaktId: string) {
    console.log('🔍 Loading client from kontakt ID:', kontaktId);
    
    // Get the kontakt first, then get the associated kunde
    this.kundeService.getKontakt(kontaktId).subscribe({
      next: (response) => {
        console.log('📋 Kontakt data received:', response.data);
        const kontakt = response.data;
        if (kontakt && kontakt.kunde_id) {
          console.log('📋 Found kontakt, loading associated kunde:', kontakt.kunde_id, 'Type:', typeof kontakt.kunde_id);
          
          // Load the client and wait for it to complete before setting form value
          this.kundeService.getKundeByUuid(kontakt.kunde_id).subscribe({
            next: (clientResponse) => {
              this.selectedClient = clientResponse.data;
              this.kundeId = clientResponse.data.uuid;
              console.log('✅ Loaded client from kontakt:', this.selectedClient?.suchbegriff);
              
              // Store the client UUID in the form after loading
              this.standortForm.patchValue({ 
                kunde_id: clientResponse.data.uuid
              });
              console.log('✅ Form updated with client UUID from kontakt:', clientResponse.data.uuid);
            },
            error: (clientError) => {
              console.error('❌ Error loading client from kontakt:', clientError);
              this.snackBar.open(this.translate.instant('STANDORT_EDIT.ERROR_LOAD_CLIENT'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
            }
          });
        } else {
          console.warn('⚠️ Kontakt has no kunde_id:', kontakt);
        }
      },
      error: (error) => {
        console.error('❌ Error loading kontakt:', error);
        this.snackBar.open(this.translate.instant('STANDORT_EDIT.ERROR_LOAD_CONTACT'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      }
    });
  }

  createForm() {
    this.standortForm = this.formBuilder.group({
      // nummer is not included in the form - it will be auto-generated by the database
      name: ['', [Validators.required]],
      status: [1],
      kunde_id: ['', [Validators.required]], // Direct client relationship
      techniker_id: [''], // Foreign key to mitarbeiter table (where is_techniker = true)
      bemerkung_struktur_anmerkung: [''],
      bericht_sprache: ['de'], // Default to Deutsch
      anmeldung_besuche: [''],
      bericht_versand: [''],
      besuchsbericht: [1], // Default checked for new standorte
      mit_auswertung_tabelle: [1], // Default checked for new standorte
      herkunft: [''],
      anrede: [''],
      bericht_email: [''], // JSON array of report contacts (no validation needed)
      // New date fields
      datum_ab_wann: [''],
      datum_bis: [''],
      // Address fields
      strasse: [''],
      plz: [''],
      ort: [''],
      land: ['']
    });
    
    console.log('📝 Form created with validators:');
    console.log('📝 name: required');
    console.log('📝 kunde_id: required');
    console.log('📝 Initial form valid:', this.standortForm.valid);
    console.log('📝 Initial form errors:', this.standortForm.errors);
    
    return this.standortForm;
  }

  /**
   * Load the billing agreement (Rechnungsvertrag) for the Abrechnung tab.
   */
  loadBilling(standortId?: string): void {
    if (!standortId) {
      this.rechnungsvertrag = null;
      return;
    }
    this.billingLoading = true;
    this.rechnungsvertragService.getRechnungsvertraege({ standort_id: standortId }).subscribe({
      next: (res) => {
        this.billingLoading = false;
        this.rechnungsvertrag = res.result === 1 && res.data && res.data.length > 0 ? res.data[0] : null;
      },
      error: () => {
        this.billingLoading = false;
        this.rechnungsvertrag = null;
      },
    });
  }

  /**
   * Create a new billing agreement (Rechnungsvertrag) for this standort.
   * The Erf.-Nr. is generated server-side as a placeholder to reconcile later.
   */
  createBilling(): void {
    const standortId = this.standort?.id?.toString();
    if (!standortId) {
      return;
    }
    this.billingSaving = true;
    this.rechnungsvertragService.createRechnungsvertrag({ standort_id: standortId }).subscribe({
      next: (res) => {
        this.billingSaving = false;
        if (res.result === 1) {
          this.rechnungsvertrag = res.data;
          this.billingEdit = true;
          this.snackBar.open('Rechnungsvertrag angelegt', 'OK', { duration: 2500 });
        } else {
          this.snackBar.open(res.error || 'Fehler beim Anlegen des Rechnungsvertrags', 'OK', { duration: 5000 });
        }
      },
      error: () => {
        this.billingSaving = false;
        this.snackBar.open('Fehler beim Anlegen des Rechnungsvertrags', 'OK', { duration: 5000 });
      },
    });
  }

  saveBilling(): void {
    if (!this.rechnungsvertrag?.id) {
      return;
    }
    this.billingSaving = true;
    const rv = this.rechnungsvertrag;
    const payload: Partial<Rechnungsvertrag> = {
      erf_nr: rv.erf_nr,
      rg_intervall_type: rv.rg_intervall_type,
      vk_wert_netto: rv.vk_wert_netto,
      vk_wert_brutto: rv.vk_wert_brutto,
      kd_bestellnr: rv.kd_bestellnr,
      zahlungsziel_tage: rv.zahlungsziel_tage,
      rg_adresse_zeile1: rv.rg_adresse_zeile1,
      rg_adresse_zuhanden: rv.rg_adresse_zuhanden,
      rg_adresse_zusatzname: rv.rg_adresse_zusatzname,
    };
    this.rechnungsvertragService.updateRechnungsvertrag(rv.id, payload).subscribe({
      next: (res) => {
        this.billingSaving = false;
        if (res.result === 1) {
          this.rechnungsvertrag = res.data;
          this.billingEdit = false;
          this.snackBar.open('Abrechnung gespeichert', 'OK', { duration: 2500 });
        } else {
          this.snackBar.open(res.error || 'Fehler beim Speichern', 'OK', { duration: 5000 });
        }
      },
      error: () => {
        this.billingSaving = false;
        this.snackBar.open('Fehler beim Speichern der Abrechnung', 'OK', { duration: 5000 });
      },
    });
  }

  loadStandort(id: string) {
    console.log('Loading location with ID:', id);
    this.isLoading = true;
    this.subscription = this.standortService.getStandort(id).subscribe(
      (response) => {
        console.log('Location data received:', response);
        this.standort = response.data;
        this.loadBilling(response.data.id?.toString());
        console.log('Setting form values with:', response.data);
        console.log('🔍 Full standort data structure:', JSON.stringify(response.data, null, 2));
        
        // Always populate form with standort data (needed for edit mode)
        const formData = {
          // nummer is not included in form data - it's auto-generated by database
          name: response.data.name || '',
          status: response.data.status || 1,
          kunde_id: response.data.kunde_id || '', // Direct client relationship
          techniker_id: response.data.techniker_id ? String(response.data.techniker_id) : '', // Foreign key to mitarbeiter table (convert to string for HTML select)
          bemerkung_struktur_anmerkung: response.data.bemerkung_struktur_anmerkung || '',
          bericht_sprache: response.data.bericht_sprache || 'de',
          anmeldung_besuche: response.data.anmeldung_besuche || '',
          bericht_versand: response.data.bericht_versand || '',
          besuchsbericht: response.data.besuchsbericht || 0,
          mit_auswertung_tabelle: response.data.mit_auswertung_tabelle || 0,
          herkunft: response.data.herkunft || '',
          anrede: response.data.anrede || '',
          bericht_email: response.data.bericht_email || '',
          // New date fields - convert to ISO format for date inputs
          datum_ab_wann: this.convertToIsoDate(response.data.datum_ab_wann) || 
            (response.data.created_at ? this.convertToIsoDate(response.data.created_at) : ''),
          datum_bis: this.convertToIsoDate(response.data.datum_bis) || '',
          // Address fields
          strasse: response.data.strasse || '',
          plz: response.data.plz || '',
          ort: response.data.ort || '',
          land: response.data.land || ''
        };
        
        console.log('Mapped form data:', formData);
        this.standortForm.patchValue(formData);
        console.log('Form values after patch:', this.standortForm.value);
        console.log('Form valid after patch:', this.standortForm.valid);
        console.log('Form errors after patch:', this.standortForm.errors);
        
        // Load client information directly from standort data
        console.log('🔍 Standort data received:', response.data);
        console.log('🔍 Has kunde data:', !!response.data.kunde);
        console.log('🔍 Has kunde_id:', !!response.data.kunde_id);
        console.log('🔍 kunde_id value:', response.data.kunde_id);
        
        if (response.data.kunde) {
          this.selectedClient = response.data.kunde;
          this.kundeId = response.data.kunde.uuid; // Use UUID, not integer ID
          console.log('✅ Client loaded directly from standort:', this.selectedClient?.suchbegriff);
          
          // Load contacts for the client
          this.loadContacts();
        } else if (response.data.kunde_id) {
          // Load client directly using kunde_id
          console.log('🔄 Loading client directly using kunde_id:', response.data.kunde_id);
          this.loadClient(response.data.kunde_id);
          // Ensure form holds the client UUID
          this.standortForm.patchValue({ kunde_id: response.data.kunde_id });
          // Also set the kundeId property for consistency
          this.kundeId = response.data.kunde_id;
          console.log('🔍 Set kundeId to:', this.kundeId);
          console.log('🔍 Form kunde_id value:', this.standortForm.get('kunde_id')?.value);
        } else {
          console.warn('⚠️ No client information found in standort data');
        }
        
        // Load all clients for dropdown (needed for edit mode)
        this.loadClients();
        
        // Load all technicians for dropdown (needed for edit mode)
        this.loadTechnicians();
        
        // Load objekt struktur
        this.loadObjektStruktur();
        
        // Load room indicators
        this.loadRoomIndicators();
        
        // Load report contacts
        this.loadReportContacts();
        
        this.isLoading = false;
      },
      (error) => {
        console.error('Error loading location:', error);
        this.isLoading = false;
        this.snackBar.open(`${this.translate.instant('STANDORT_EDIT.ERROR_LOAD_STANDORT')}: ${error.status} ${error.statusText}`, this.translate.instant('COMMON.CLOSE'), {
          duration: 5000,
        });
      }
    );
  }

  saveStandort() {
    console.log('💾 === SAVE ATTEMPT DEBUG ===');
    console.log('💾 Form valid:', this.standortForm.valid);
    console.log('💾 Form invalid:', this.standortForm.invalid);
    console.log('💾 Form errors:', this.standortForm.errors);
    console.log('💾 Form value:', this.standortForm.value);
    
    // Debug each field before save
    Object.keys(this.standortForm.controls).forEach(key => {
      const control = this.standortForm.get(key);
      if (control && control.invalid) {
        console.log(`💾 INVALID FIELD "${key}":`, {
          errors: control.errors,
          value: control.value,
          touched: control.touched
        });
      }
    });
    
    if (this.standortForm.valid) {
      this.isSaving = true;
      const standortData = { ...this.standortForm.value } as any;

      // Normalize status to numeric ID
      if (typeof standortData.status === 'object' && standortData.status !== null) {
        standortData.status = standortData.status.id ?? standortData.status.value ?? standortData.status;
      }
      if (typeof standortData.status === 'string') {
        const parsed = parseInt(standortData.status, 10);
        if (!Number.isNaN(parsed)) standortData.status = parsed;
      }

      // Normalize techniker_id to numeric ID (API expects number, but form uses string for HTML select)
      if (standortData.techniker_id) {
        const technikerIdNum = Number(standortData.techniker_id);
        if (!isNaN(technikerIdNum) && technikerIdNum > 0) {
          standortData.techniker_id = technikerIdNum;
        } else {
          standortData.techniker_id = null; // Clear invalid value
        }
      } else {
        standortData.techniker_id = null; // Ensure empty string becomes null
      }

      // Ensure kunde_id holds CLIENT UUID
      console.log('🔍 Save validation - selectedClient:', this.selectedClient);
      console.log('🔍 Save validation - selectedClient?.uuid:', this.selectedClient?.uuid);
      console.log('🔍 Save validation - standortData.kunde_id:', standortData.kunde_id);
      console.log('🔍 Save validation - kundeId:', this.kundeId);
      
      if (this.selectedClient?.uuid) {
        standortData.kunde_id = this.selectedClient.uuid;
        console.log('✅ Using selectedClient UUID:', this.selectedClient.uuid);
      } else if (standortData.kunde_id) {
        // If value exists, verify it matches a known client UUID; otherwise force user selection
        const matchesClient = this.clients.some(c => c.uuid === standortData.kunde_id);
        console.log('🔍 Checking if kunde_id matches known client:', matchesClient);
        console.log('🔍 Available client UUIDs:', this.clients.map(c => c.uuid));
        if (!matchesClient) {
          this.isSaving = false;
          this.snackBar.open(this.translate.instant('STANDORT_EDIT.ERROR_SELECT_CLIENT'), this.translate.instant('COMMON.CLOSE'), { duration: 4000 });
          return;
        }
        console.log('✅ Using form kunde_id value:', standortData.kunde_id);
      } else {
        this.isSaving = false;
        this.snackBar.open(this.translate.instant('STANDORT_EDIT.ERROR_SELECT_CLIENT'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
        return;
      }
      
      // Handle empty date fields - convert empty strings to null
      if (standortData.datum_ab_wann === '') {
        standortData.datum_ab_wann = null;
      }
      if (standortData.datum_bis === '') {
        standortData.datum_bis = null;
      }
      
      console.log('💾 Saving standort with data:', standortData);
      console.log('💾 kunde_id value:', standortData.kunde_id);
      console.log('💾 techniker_id value:', standortData.techniker_id);
      console.log('💾 bericht_email value:', standortData.bericht_email);
      console.log('💾 datum_ab_wann value:', standortData.datum_ab_wann);
      console.log('💾 datum_bis value:', standortData.datum_bis);
      console.log('💾 Selected client:', this.selectedClient);
      console.log('💾 Selected client UUID:', this.selectedClient?.uuid);
      
      // nummer is not included - it will be auto-generated by the database

      const saveOperation = this.isNewStandort
        ? this.standortService.createStandort(standortData)
        : this.standortService.updateStandort(this.standort!.id, standortData);

      saveOperation.subscribe(
        (response) => {
          this.isSaving = false;
          
          // Log journal entry with entity IDs
          const entityIds = this.journalService.extractEntityIds(response.data);
          this.journalService.log(
            this.isNewStandort ? 'Standort erstellt' : 'Standort aktualisiert',
            '',
            `Standort: ${response.data?.name || 'Unbekannt'}`,
            entityIds
          );
          
          this.snackBar.open(
            this.translate.instant(this.isNewStandort ? 'STANDORT_EDIT.SUCCESS_CREATED' : 'STANDORT_EDIT.SUCCESS_UPDATED'),
            this.translate.instant('COMMON.CLOSE'),
            { duration: 3000 }
          );
          
          if (this.isNewStandort) {
            // For new standorte, navigate to the detail view of the newly created standort
            const newStandortId = response.data.id;
            if (newStandortId) {
              // Navigate to the detail view (first tab: Standort Information) of the newly created standort
              this.router.navigate(['/standort-edit', newStandortId], {
                queryParams: { tab: 'information' } // Ensure we're on the first tab (Standort Information)
              });
            } else {
              // Fallback: navigate to list if ID is missing
              console.error('No standort ID returned from create operation');
              this.router.navigate(['/standorte']);
            }
          } else {
            // For existing standorte, stay in view mode
            this.isEditMode = false;
            this.standort = response.data; // Update local data
            console.log('✅ Switched to view mode after save');
          }
        },
        (error) => {
          this.isSaving = false;
          this.snackBar.open(
            this.translate.instant(this.isNewStandort ? 'STANDORT_EDIT.ERROR_CREATE' : 'STANDORT_EDIT.ERROR_UPDATE'),
            this.translate.instant('COMMON.CLOSE'),
            { duration: 3000 }
          );
        }
      );
    }
  }

  cancel() {
    if (this.isNewStandort) {
      // For new standorte, navigate back based on context
      const returnTo = this.route.snapshot.queryParams['returnTo'];
      const kundeId = this.route.snapshot.queryParams['kundeId'];
      
      if (returnTo === 'client-edit' && kundeId) {
        // Return to client-edit with the specific kundeId
        this.router.navigate(['/client-edit', kundeId]);
      } else {
        // Default navigation back to standorte list
        this.router.navigate(['/standorte']);
      }
    } else {
      // For existing standorte, switch back to view mode
      this.isEditMode = false;
      console.log('✅ Switched to view mode after cancel');
    }
  }

  goBackToList() {
    // Check if we should return to a specific context (e.g., client-edit)
    const returnTo = this.route.snapshot.queryParams['returnTo'];
    const kundeId = this.route.snapshot.queryParams['kundeId'];
    
    if (returnTo === 'client-edit' && kundeId) {
      // Return to client-edit with the specific kundeId
      this.router.navigate(['/client-edit', kundeId]);
    } else {
      // Default navigation back to standorte list
      this.router.navigate(['/standorte']);
    }
  }

  createPlan() {
    console.log('📋 Creating new plan for standort:', this.standortId);
    
    if (!this.standortId) {
      this.snackBar.open(this.translate.instant('STANDORT_EDIT.ERROR_NO_STANDORT'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      return;
    }

    // Navigate to standalone plan-edit route
    this.router.navigate(['/plan', 'new'], {
      queryParams: {
        standortId: this.standortId,
        mode: 'create',
        parentContext: 'standort-edit',
        returnTo: 'standort-edit',
        standortUuid: this.standortId
      }
    });
  }

  deleteStandort() {
    if (!this.standort) {
      this.snackBar.open(this.translate.instant('STANDORT_EDIT.ERROR_NO_STANDORT'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Standort löschen',
        message:
          `Möchten Sie den Standort "${this.standort.name}" wirklich löschen?\n\n` +
          `Folgende Aktionen werden durchgeführt:\n` +
          `- Der Standort wird als inaktiv markiert\n` +
          `- Die aktive Projektaufgabe wird als inaktiv markiert\n` +
          `- Alle aktiven Kontrolltermine werden als gelöscht markiert\n\n` +
          `Hinweis: Alle Daten werden als inaktiv markiert und nicht dauerhaft gelöscht.\n` +
          `Die Daten bleiben für Audit-Zwecke erhalten und können bei Bedarf wiederhergestellt werden.`,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.standortService.deleteStandort(this.standort!.id).subscribe(
          (response) => {
            // Log journal entry with entity IDs
            const entityIds = this.journalService.extractEntityIds(this.standort);
            this.journalService.log(
              'Standort gelöscht (deaktiviert)',
              '',
              `Standort: ${this.standort?.name || 'Unbekannt'}`,
              entityIds
            );
            
            this.snackBar.open(this.translate.instant('STANDORT_EDIT.SUCCESS_DEACTIVATED'), this.translate.instant('COMMON.CLOSE'), {
              duration: 3000,
            });
            // Navigate back based on context
            const returnTo = this.route.snapshot.queryParams['returnTo'];
            const kundeId = this.route.snapshot.queryParams['kundeId'];
            
            if (returnTo === 'client-edit' && kundeId) {
              this.router.navigate(['/client-edit', kundeId]);
            } else {
              this.router.navigate(['/standorte']);
            }
          },
          (error) => {
            console.error('Error deleting location:', error);
            this.snackBar.open(this.translate.instant('STANDORT_EDIT.ERROR_DELETE'), this.translate.instant('COMMON.CLOSE'), {
              duration: 5000,
            });
          }
        );
      }
    });
  }

  isFieldInvalid(fieldName: string): boolean {
    const control = this.standortForm.get(fieldName);
    return control ? control.invalid && control.touched : false;
  }

  getStatusText(statusId: number | any): string {
    if (typeof statusId === 'object' && statusId !== null) {
      // If status is an object, return its label or value
      return statusId.label || statusId.value || 'Unbekannt';
    }
    
    // If status is a number, find it in the environment array
    const statusArray = (environment as any).standorte_status || [];
    const status = statusArray.find((s: any) => s.id === statusId);
    return status ? status.label : 'Unbekannt';
  }



  getBerichtSpracheText(spracheValue: string): string {
    const spracheArray = (environment as any).bericht_sprache || [];
    const sprache = spracheArray.find((s: any) => s.value === spracheValue);
    return sprache ? sprache.label : spracheValue;
  }

  enterEditMode() {
    this.isEditMode = true;
    
    // Load all active clients for dropdown in edit mode
    this.loadClients();
    
    // Load all technicians for dropdown in edit mode
    // Note: techniker_id will be patched AFTER technicians are loaded (in loadTechnicians callback)
    this.loadTechnicians();
    
    // Patch the form with current location data
    // Note: techniker_id is handled separately in loadTechnicians() after technicians are loaded
    if (this.standort) {
      this.standortForm.patchValue({
        // nummer is not included in form - it's auto-generated by database
        name: this.standort.name || '',
        status: this.standort.status || 1,
        kunde_id: this.standort.kunde_id || '',
        // techniker_id is NOT patched here - it will be patched in loadTechnicians() after technicians load
        bemerkung_struktur_anmerkung: this.standort.bemerkung_struktur_anmerkung || '',
        bericht_sprache: this.standort.bericht_sprache || 'de',
        anmeldung_besuche: this.standort.anmeldung_besuche || '',
        bericht_versand: this.standort.bericht_versand || '',
        besuchsbericht: this.standort.besuchsbericht || 0,
        mit_auswertung_tabelle: this.standort.mit_auswertung_tabelle || 0,
        herkunft: this.standort.herkunft || '',
        anrede: this.standort.anrede || '',
        bericht_email: this.standort.bericht_email || '',
        // New date fields - convert to ISO format for date inputs
        datum_ab_wann: this.convertToIsoDate(this.standort.datum_ab_wann) || 
          (this.standort.created_at ? this.convertToIsoDate(this.standort.created_at) : ''),
        datum_bis: this.convertToIsoDate(this.standort.datum_bis) || '',
        // Address fields
        strasse: this.standort.strasse || '',
        plz: this.standort.plz || '',
        ort: this.standort.ort || '',
        land: this.standort.land || ''
      });
      
      console.log('🔧 Form patched in edit mode with kunde_id:', this.standort.kunde_id);
    }
  }



  /**
   * Load all available technicians for dropdown
   */
  loadTechnicians(): void {
    this.isLoadingTechnicians = true;
    // In edit mode we must include inactive Techniker so existing relationships can still be displayed/saved.
    const includeInactive = this.isEditMode === true;
    this.mitarbeiterService.getTechnicians(includeInactive).subscribe({
      next: (response) => {
        this.availableTechnicians = response.data || [];
        this.isLoadingTechnicians = false;

        // Fix: if a techniker is already assigned but not in the dropdown options (e.g. inactive),
        // keep it visible/selectable so the form doesn't reset to "Kein Techniker zugewiesen".
        const currentIdRaw = this.standortForm.get('techniker_id')?.value ?? this.standort?.techniker_id;
        const currentId = currentIdRaw ? Number(currentIdRaw) : null;
        if (currentId && !this.availableTechnicians.some(t => t.id === currentId)) {
          // Add inactive techniker to the list so it can be selected
          this.availableTechnicians = [
            ...this.availableTechnicians,
            { id: currentId, display_name: '(inaktiv)', nummer: currentId, funktion: 'Techniker' }
          ];
          console.log('✅ Added inactive techniker to list:', currentId);
        }

        // FIX: Ensure form value is set AFTER technicians are loaded with proper type conversion
        // This fixes the issue where techniker doesn't show in dropdown when entering edit mode
        // HTML select elements work with strings, so we need to convert to string for proper matching
        if (this.standort?.techniker_id) {
          const technikerId = Number(this.standort.techniker_id);
          if (!isNaN(technikerId) && technikerId > 0) {
            // Check if this techniker exists in the available list
            const technikerExists = this.availableTechnicians.some(t => t.id === technikerId);
            if (technikerExists) {
              // Convert to string to match HTML select option values (which are strings)
              const technikerIdString = technikerId.toString();
              // Use setTimeout to ensure DOM is ready for the select element
              setTimeout(() => {
                // Use setValue on the specific control to ensure it updates
                const technikerControl = this.standortForm.get('techniker_id');
                if (technikerControl) {
                  technikerControl.setValue(technikerIdString, { emitEvent: false });
                  
                  // Force change detection to update the select element
                  this.changeDetectorRef.detectChanges();
                }
              }, 100); // Small delay to ensure select element is rendered
            } else {
              console.warn('⚠️ Techniker ID', technikerId, 'not found in available technicians list');
            }
          }
        }
      },
      error: (error) => {
        console.error('Error loading technicians:', error);
        this.snackBar.open(this.translate.instant('STANDORT_EDIT.ERROR_LOAD_TECHNICIANS'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
        this.isLoadingTechnicians = false;
      }
    });
  }

  /**
   * Load all available contacts for the selected client
   */
  loadContacts(): void {
    if (!this.kundeId) {
      console.log('No kunde ID available for loading contacts');
      this.availableContacts = [];
      return;
    }

    this.isLoadingContacts = true;
    this.kontaktService.getKontakteByKundeId(this.kundeId, 1, 100).subscribe({
      next: (response) => {
        // Only active Kontakte should be selectable for report-recipient settings (status_id === 1)
        this.availableContacts = (response.data || []).filter((c: any) => c?.status_id === 1);
        this.isLoadingContacts = false;
      },
      error: (error) => {
        console.error('Error loading contacts:', error);
        this.snackBar.open(this.translate.instant('STANDORT_EDIT.ERROR_LOAD_CONTACTS'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
        this.isLoadingContacts = false;
        this.availableContacts = [];
      }
    });
  }

  /**
   * Get technician display name by ID
   */
  getTechnicianDisplayName(technikerId: string | number): string {
    if (!technikerId) {
      return '-';
    }
    const technician = this.availableTechnicians.find(t => t.id === Number(technikerId));
    return technician ? `${technician.display_name} (${technician.nummer}) - ${technician.funktion}` : `ID: ${technikerId}`;
  }

  /**
   * Handle contact selection from dropdown
   */
  onContactSelected(contact: Kontakt): void {
    if (contact && contact.email) {
      console.log('Contact selected:', contact.name, 'Email:', contact.email);
      this.standortForm.patchValue({
        bericht_email: contact.email
      });
    }
  }

  /**
   * Handle contact selection from dropdown by ID
   */
  onContactSelectedById(contactId: string): void {
    if (contactId) {
      const contact = this.availableContacts.find(c => c.id === contactId);
      if (contact) {
        this.onContactSelected(contact);
      }
    }
  }

  /**
   * Get contact display name for dropdown
   */
  getContactDisplayName(contact: Kontakt): string {
    if (!contact) return '';
    
    let displayName = contact.name || 'Unbekannter Kontakt';
    if (contact.email) {
      displayName += ` (${contact.email})`;
    }
    return displayName;
  }

  /**
   * Load report contacts from standort data
   */
  loadReportContacts(): void {
    console.log('🔍 Loading report contacts...');
    console.log('🔍 Standort bericht_email:', this.standort?.bericht_email);
    console.log('🔍 Type:', typeof this.standort?.bericht_email);
    
    if (this.standort?.bericht_email) {
      try {
        // Handle new array format
        if (Array.isArray(this.standort.bericht_email)) {
          this.reportContacts = this.standort.bericht_email;
          console.log('✅ Loaded array format contacts:', this.reportContacts);
        } else if (typeof this.standort.bericht_email === 'string') {
          // Check if it's JSON string
          if (this.standort.bericht_email.startsWith('[') || this.standort.bericht_email.startsWith('{')) {
            try {
              this.reportContacts = JSON.parse(this.standort.bericht_email);
              console.log('✅ Parsed JSON string contacts:', this.reportContacts);
            } catch (parseError) {
              console.error('Error parsing JSON string:', parseError);
              // Fallback to old string format
              this.reportContacts = [{
                email: this.standort.bericht_email,
                name: '',
                anrede: ''
              }];
            }
          } else {
            // Handle old string format (backward compatibility)
            this.reportContacts = [{
              email: this.standort.bericht_email,
              name: '',
              anrede: ''
            }];
            console.log('✅ Loaded old string format contact:', this.reportContacts);
          }
        }
      } catch (e) {
        console.error('Error parsing bericht_email:', e);
        this.reportContacts = [];
      }
    } else {
      this.reportContacts = [];
    }
    
    console.log('🔍 Final reportContacts:', this.reportContacts);
    
    // Update the form with the current contacts
    this.updateFormWithContacts();
  }

  /**
   * Add new contact to the list
   */
  addContact(): void {
    if (this.newContactEmail) {
      this.reportContacts.push({
        email: this.newContactEmail,
        name: this.newContactName || '',
        anrede: this.newContactAnrede || ''
      });
      
      // Clear the input fields
      this.newContactEmail = '';
      this.newContactName = '';
      this.newContactAnrede = '';
      
      // Update the form with JSON
      this.updateFormWithContacts();
    }
  }

  /**
   * Remove contact from the list
   */
  removeContact(index: number): void {
    this.reportContacts.splice(index, 1);
    this.updateFormWithContacts();
  }

  /**
   * Update form with current contacts as JSON
   */
  updateFormWithContacts(): void {
    this.standortForm.patchValue({
      bericht_email: JSON.stringify(this.reportContacts)
    });
  }

  /**
   * Handle contact selection from dropdown (for adding to list)
   */
  onContactSelectedForList(contact: Kontakt): void {
    if (contact && contact.email) {
      // Verify contact is active before allowing selection
      if (contact.status_id !== 1) {
        console.warn('Attempted to select inactive kontakt:', contact.id, contact.name);
        this.snackBar.open(this.translate.instant('STANDORT_EDIT.ERROR_INACTIVE_CONTACT'), this.translate.instant('COMMON.CLOSE'), { 
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom'
        });
        return;
      }
      this.newContactEmail = contact.email;
      this.newContactName = contact.name;
      this.newContactAnrede = `Sehr geehrte/r ${contact.name}!`;
    }
  }

  /**
   * Handle contact selection from dropdown by ID (for adding to list)
   */
  onContactSelectedForListById(contactId: string): void {
    if (contactId) {
      const contact = this.availableContacts.find(c => c.id === contactId);
      if (contact) {
        // Double-check that the contact is active (status_id === 1)
        if (contact.status_id !== 1) {
          console.warn('Attempted to select inactive kontakt:', contact.id, contact.name);
          this.snackBar.open(this.translate.instant('STANDORT_EDIT.ERROR_INACTIVE_CONTACT'), this.translate.instant('COMMON.CLOSE'), { 
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'bottom'
          });
          return;
        }
        this.onContactSelectedForList(contact);
      }
    }
  }

  /**
   * Optional email validator - only validates if email is provided
   */
  optionalEmailValidator(control: any) {
    if (!control.value || control.value.trim() === '') {
      return null; // No validation error for empty values
    }
    
    // Use the built-in email validator for non-empty values
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(control.value) ? null : { email: true };
  }

  cancelEdit() {
    this.isEditMode = false;
  }

  // Objekt-Struktur methods
  loadObjektStruktur(): void {
    this.objektStrukturLoading = true;
    
    this.objektStrukturService.getFullObjektStruktur(this.standortId!)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.objektStrukturData = response.data;
          this.objektStrukturLoading = false;
        },
        error: (error) => {
          console.error('Error loading objekt struktur:', error);
          this.objektStrukturLoading = false;
        }
      });
  }



  /**
   * Handle tab change - reload indicators when Indikatoren tab is opened
   */
  onTabChange(index: number): void {
    // Tab indices: 0=Information, 1=Objekt-Struktur, 2=Plan Dateien, 3=Pläne, 4=Indikatoren
    // Check if Indikatoren tab (index 4) is selected
    if (index === 4) {
      this.loadRoomIndicators();
    }
  }

  /**
   * Handle structure changes from objekt-struktur component
   * Reload indicators list when indicators are added, updated, or deleted
   */
  onStructureChanged(): void {
    // Reload indicators if we're currently on the Indikatoren tab
    // We'll check the current tab index by looking at the tabGroup
    if (this.tabGroup && this.tabGroup.selectedIndex === 4) {
      this.loadRoomIndicators();
    }
  }

  /**
   * Fetch all room indicators across all pages (if paginated)
   */
  private fetchAllRoomIndicators(): void {
    if (!this.standortId) {
      return;
    }
    
    // Fetch with a very high limit to get all items in one request
    this.objektStrukturService.getRoomIndikatorenByStandort(this.standortId, 10000).subscribe({
      next: (response: any) => {
        let indicators: any[] = [];
        if (Array.isArray(response)) {
          indicators = response as any;
        } else if (response.data && Array.isArray(response.data)) {
          indicators = response.data as any;
        }
        
        this.roomIndicators = indicators;
        console.log('All room indicators loaded:', this.roomIndicators.length, 'indicators');
        this.applyStatusFilter();
      },
      error: (error) => {
        console.error('Error loading all room indicators:', error);
        this.snackBar.open(this.translate.instant('STANDORT_EDIT.ERROR_LOAD_INDICATORS'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
        this.roomIndicators = [];
      }
    });
  }

  loadRoomIndicators(): void {
    // Load room indicators for this standort
    if (!this.standortId) {
      console.warn('No standort ID available for loading room indicators');
      this.roomIndicators = [];
      return;
    }

    // Use a very high limit to get all indicators in one request
    this.objektStrukturService.getRoomIndikatorenByStandort(this.standortId, 10000).subscribe({
      next: (response: any) => {
        // Handle both array response and paginated response
        let indicators: any[] = [];
        if (Array.isArray(response)) {
          indicators = response as any;
        } else if (response.data && Array.isArray(response.data)) {
          indicators = response.data as any;
        } else {
          indicators = [];
        }
        
        // Check if there's pagination and we need to fetch more pages
        if (response.pagination && response.pagination.total > indicators.length) {
          console.log(`⚠️ Pagination detected: ${indicators.length} of ${response.pagination.total} indicators loaded. Fetching all pages...`);
          // If we have pagination and more items, fetch all pages
          this.fetchAllRoomIndicators();
          return;
        }
        
        this.roomIndicators = indicators;
        this.applyStatusFilter();
      },
      error: (error) => {
        console.error('Error loading room indicators:', error);
        this.snackBar.open(this.translate.instant('STANDORT_EDIT.ERROR_LOAD_INDICATORS'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
        this.roomIndicators = [];
      }
    });
  }

  /**
   * Check if indicator has active parent chain (room, sektion, gebaeude all active)
   * This doesn't check the indicator's own status
   */
  hasActiveParentChain(indicator: RoomIndicator): boolean {
    // Check room status
    if (!indicator.room) {
      return false;
    }
    const roomStatus = Number((indicator.room as any).status);
    if (roomStatus !== 1) {
      return false;
    }
    
    // Check sektion status
    if (!indicator.room?.sektion) {
      return false;
    }
    const sektionStatus = Number((indicator.room.sektion as any).status);
    if (sektionStatus !== 1) {
      return false;
    }
    
    // Check gebaeude status
    if (!indicator.room?.sektion?.gebaeude) {
      return false;
    }
    const gebaeudeStatus = Number((indicator.room.sektion.gebaeude as any).status);
    if (gebaeudeStatus !== 1) {
      return false;
    }
    
    return true;
  }

  /**
   * Check if indicator is effectively active (indicator and all parents are active)
   */
  isIndicatorEffectivelyActive(indicator: RoomIndicator): boolean {
    // Check indicator status
    const indicatorStatus = indicator.status;
    if (indicatorStatus !== 1 && indicatorStatus !== true) {
      return false;
    }
    
    // Check parent chain
    return this.hasActiveParentChain(indicator);
  }

  applyStatusFilter(): void {
    if (!this.roomIndicators) {
      this.filteredRoomIndicators = [];
      return;
    }
    
    let filtered: RoomIndicator[];
    
    // Apply filter based on selected filter
    if (this.selectedStatusFilter === 1) {
      // "Aktiv": Only show effectively active indicators (active indicator + active parents)
      // This requires both active indicator AND active parent chain
      filtered = this.roomIndicators.filter((ri: RoomIndicator) => {
        return this.isIndicatorEffectivelyActive(ri);
      });
    } else if (this.selectedStatusFilter === 0) {
      // "Inaktiv": Show inactive indicators OR indicators with inactive parents
      filtered = this.roomIndicators.filter((ri: RoomIndicator) => {
        const statusVal = (ri as any)?.status;
        // Normalize status to boolean/number for comparison
        const normalizedStatus = statusVal === 1 || statusVal === true || statusVal === '1' || statusVal === 'true' || statusVal === 'True';
        const isIndicatorInactive = !normalizedStatus;
        const hasInactiveParent = !this.hasActiveParentChain(ri);
        // Show if indicator is inactive OR has inactive parent
        return isIndicatorInactive || hasInactiveParent;
      });
    } else {
      // "Alle": Show ALL indicators (no filtering by status or parent chain)
      filtered = [...this.roomIndicators];
    }
    
    // Sort by indikator_number
    this.filteredRoomIndicators = this.sortIndicatorsByNumber(filtered);
  }

  private sortIndicatorsByNumber(indicators: RoomIndicator[]): RoomIndicator[] {
    return [...indicators].sort((a, b) => {
      const numA = a.indikator_number ?? 999999;
      const numB = b.indikator_number ?? 999999;
      return numA - numB;
    });
  }

  onChangeStatusFilter(event: any): void {
    const val = event?.target?.value;
    this.selectedStatusFilter = typeof val === 'string' ? parseInt(val, 10) : val;
    if (Number.isNaN(this.selectedStatusFilter as any)) {
      this.selectedStatusFilter = 1;
    }
    this.applyStatusFilter();
  }

  toggleStatusMenu(): void {
    this.isStatusMenuOpen = !this.isStatusMenuOpen;
  }

  closeStatusMenu(): void {
    this.isStatusMenuOpen = false;
  }

  selectStatusFilter(value: number): void {
    this.selectedStatusFilter = value;
    this.applyStatusFilter();
    this.isStatusMenuOpen = false;
  }

  getStatusFilterLabel(): string {
    switch (Number(this.selectedStatusFilter)) {
      case 1: return 'Aktiv';
      case 0: return 'Inaktiv';
      default: return 'Alle';
    }
  }

  /**
   * Check if indicator is active but has inactive parent chain
   */
  isActiveButInactiveParent(indicator: RoomIndicator): boolean {
    // Check if indicator itself is active
    const indicatorStatus = indicator.status;
    const isIndicatorActive = indicatorStatus === 1 || indicatorStatus === true;
    
    if (!isIndicatorActive) {
      return false; // Indicator is not active, so this doesn't apply
    }
    
    // Check if parent chain is inactive
    return !this.hasActiveParentChain(indicator);
  }

  getStatusTextSimple(indicator: RoomIndicator): string {
    // Check if indicator is active but has inactive parent
    if (this.isActiveButInactiveParent(indicator)) {
      return 'Inaktiv Parent';
    }
    
    const status = indicator.status;
    if (status === 1 || status === true) return 'Aktiv';
    if (status === 0 || status === false) return 'Inaktiv';
    return '-';
  }

  getStatusBadgeClasses(indicator: RoomIndicator): string {
    // If indicator is active but has inactive parent, show as inactive (red)
    if (this.isActiveButInactiveParent(indicator)) {
      return 'bg-red-100 text-red-800 ring-red-600/20';
    }
    
    const status = indicator.status;
    if (status === 1 || status === true) return 'bg-green-100 text-green-800 ring-green-600/20';
    return 'bg-red-100 text-red-800 ring-red-600/20';
  }

  getSymbolUrlFromIndicator(indicator: any): string | null {
    const symbolId = (indicator as any)?.indikator?.symbol_id;
    if (!symbolId) return null;
    const key = (environment as any).indikatorSymbolMap?.[symbolId];
    if (!key) return null;
    const base = (environment as any).indikatorSymbolBasePath || '/assets/symbols';
    return `${base}/${key}.svg`;
  }

  getSymbolColorFromIndicator(indicator: any): string | null {
    const symbolId = (indicator as any)?.indikator?.symbol_id;
    if (!symbolId) return null;
    const colorMap = (environment as any).indikatorSymbolColorMap || {};
    return colorMap[symbolId] || null;
  }

  getIndikatorCode(indicator: any): string {
    return (indicator as any)?.indikator?.code || '';
  }

  formatSchwellenwertAsInteger(schwellenwert: any): string {
    if (schwellenwert === null || schwellenwert === undefined || schwellenwert === '') {
      return '-';
    }
    const numValue = parseFloat(schwellenwert);
    if (isNaN(numValue)) {
      return schwellenwert.toString();
    }
    return Math.round(numValue).toString();
  }

  getIndicatorStatusClasses(indicator: RoomIndicator): string {
    const value = parseFloat(indicator.current_value);
    const threshold = parseFloat(indicator.schwellenwert);
    
    if (isNaN(value) || isNaN(threshold)) {
      return 'bg-gray-100 text-gray-800 ring-gray-600/20';
    }
    
    const isMoreBetter = indicator.mehr_weniger === 1;
    const isAboveThreshold = value > threshold;
    const isBelowThreshold = value < threshold;
    
    if (isMoreBetter) {
      return isAboveThreshold 
        ? 'bg-green-50 text-green-700 ring-green-600/20' 
        : 'bg-red-50 text-red-700 ring-red-600/20';
    } else {
      return isBelowThreshold 
        ? 'bg-green-50 text-green-700 ring-green-600/20' 
        : 'bg-red-50 text-red-700 ring-red-600/20';
    }
  }

  getIndicatorIcon(indicator: RoomIndicator): string {
    const value = parseFloat(indicator.current_value);
    const threshold = parseFloat(indicator.schwellenwert);
    const isMoreBetter = indicator.mehr_weniger === 1;
    
    if (isNaN(value) || isNaN(threshold)) {
      return 'help_outline';
    }
    
    const isAboveThreshold = value > threshold;
    const isBelowThreshold = value < threshold;
    
    if (isMoreBetter) {
      return isAboveThreshold ? 'trending_up' : 'trending_down';
    } else {
      return isBelowThreshold ? 'trending_up' : 'trending_down';
    }
  }

  getIndicatorStatusText(indicator: RoomIndicator): string {
    const value = parseFloat(indicator.current_value);
    const threshold = parseFloat(indicator.schwellenwert);
    const isMoreBetter = indicator.mehr_weniger === 1;
    
    if (isNaN(value) || isNaN(threshold)) {
      return 'Unbekannt';
    }
    
    const isAboveThreshold = value > threshold;
    const isBelowThreshold = value < threshold;
    
    if (isMoreBetter) {
      return isAboveThreshold ? 'Gut' : 'Schlecht';
    } else {
      return isBelowThreshold ? 'Gut' : 'Schlecht';
    }
  }

  editRoomIndicator(indicator: RoomIndicator): void {
    this.snackBar.open(`${this.translate.instant('INDICATOR.EDIT')}: "${indicator.name}"`, this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
  }

  expandAll() {
    this.expandNodesRecursive(this.objektStrukturData, true);
  }

  collapseAll() {
    this.expandNodesRecursive(this.objektStrukturData, false);
  }

  private expandNodesRecursive(nodes: ObjektStrukturNode[], expanded: boolean) {
    nodes.forEach(node => {
      node.expanded = expanded;
      if (node.children && node.children.length > 0) {
        this.expandNodesRecursive(node.children, expanded);
      }
    });
  }

  addNewNode() {
    console.log('Add new node functionality to be implemented');
    this.snackBar.open(this.translate.instant('COMMON.NOT_IMPLEMENTED'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
  }

  getFormattedIndicatorNumber(indikatorNumber: number | null | undefined): string {
    if (indikatorNumber === null || indikatorNumber === undefined) {
      return '-';
    }
    return indikatorNumber.toString().padStart(3, '0');
  }

  onIndicatorAdded(): void {
    // Legacy method - now calls onStructureChanged
    this.onStructureChanged();
  }


  printRoomIndicator(indicator: RoomIndicator): void {
    console.log('Opening label preview dialog for indicator:', indicator);
    
    const dialogRef = this.dialog.open(LabelPreviewDialogComponent, {
      width: '800px',
      height: '600px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      data: {
        indicator: indicator,
        standortId: this.standortId
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('Label preview dialog closed');
    });
  }

  printAllRoomIndicators(): void {
    console.log('Printing QR labels for all indicators');
    console.log('DEBUG: standortId:', this.standortId);
    
    if (!this.standortId) {
      this.snackBar.open(this.translate.instant('STANDORT_EDIT.ERROR_NO_STANDORT'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      return;
    }
    
    // Open progress dialog
    const progressDialogRef = this.dialog.open(ProgressDialogComponent, {
      width: '300px',
      disableClose: true, // Prevent closing during operation
      data: {
        title: 'QR Labels werden generiert',
        message: 'Bitte warten Sie, während die PDF generiert wird...'
      }
    });
    
    console.log('DEBUG: About to call generateQRIndicatorLabelsPDF');
    this.standortService.generateQRIndicatorLabelsPDF(this.standortId).subscribe({
      next: (pdfBlob: Blob) => {
        // Close progress dialog
        progressDialogRef.close();
        
        // Generate and download PDF
        const url = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `qr-labels-standort-${this.standortId}-${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        const count = this.roomIndicators?.length || 0;
        this.snackBar.open(`${count} QR Labels PDF`, this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      },
      error: (error) => {
        // Close progress dialog on error
        progressDialogRef.close();
        
        console.error('Error generating PDF:', error);
        this.snackBar.open(this.translate.instant('STANDORT_EDIT.ERROR_PDF'), this.translate.instant('COMMON.CLOSE'), { duration: 5000 });
      }
    });
  }

  private openPrintDialog(htmlContent: string, title: string): void {
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>${title}</title>
          <style>
            body { 
              margin: 0; 
              padding: 20px; 
              font-family: Arial, sans-serif; 
              background: #f5f5f5;
            }
            @media print {
              body { 
                margin: 0; 
                padding: 0; 
                background: white;
              }
              .no-print { display: none; }
              .label {
                page-break-after: always;
                margin-bottom: 0;
              }
              .label:last-child {
                page-break-after: avoid;
              }
            }
            .print-button {
              position: fixed;
              top: 10px;
              right: 10px;
              background: #007bff;
              color: white;
              border: none;
              padding: 10px 20px;
              border-radius: 4px;
              cursor: pointer;
              z-index: 1000;
              font-size: 14px;
            }
            .print-button:hover {
              background: #0056b3;
            }
            .print-info {
              position: fixed;
              top: 10px;
              left: 10px;
              background: #28a745;
              color: white;
              padding: 10px 15px;
              border-radius: 4px;
              z-index: 1000;
              font-size: 14px;
            }
          </style>
        </head>
        <body>
          <div class="print-info no-print">${title} - Bereit zum Drucken</div>
          <button class="print-button no-print" onclick="window.print()">🖨️ Drucken</button>
          ${htmlContent}
        </body>
        </html>
      `);
      printWindow.document.close();
    } else {
      this.snackBar.open(this.translate.instant('STANDORT_EDIT.ERROR_POPUP_BLOCKED'), this.translate.instant('COMMON.CLOSE'), { duration: 5000 });
    }
  }

  /**
   * Report generation methods
   */
  getSelectedCategoryOption(): ReportCategoryOption | undefined {
    return this.availableReportCategories.find(c => c.value === this.selectedReportCategory);
  }

  toggleCategoryDropdown(event: Event): void {
    event.stopPropagation();
    this.isCategoryDropdownOpen = !this.isCategoryDropdownOpen;
  }

  selectCategory(category: ReportCategory, event: Event): void {
    event.stopPropagation();
    const categoryOption = this.availableReportCategories.find(c => c.value === category);
    if (categoryOption && categoryOption.available) {
      this.selectedReportCategory = category;
      this.isCategoryDropdownOpen = false;
    }
  }

  getStrukturCategories(): ReportCategoryOption[] {
    return this.availableReportCategories.filter(cat => 
      ['systemplan', 'qr_etiketten', 'indikatoren_liste', 'trend_analyse'].includes(cat.value)
    );
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    // Close category dropdown when clicking outside
    if (this.isCategoryDropdownOpen) {
      const target = event.target as HTMLElement;
      if (!target.closest('.category-dropdown-container')) {
        this.isCategoryDropdownOpen = false;
      }
    }
  }

  generateStandortReport(): void {
    if (!this.standort || !this.standortId) {
      this.snackBar.open(this.translate.instant('STANDORT_EDIT.ERROR_NO_STANDORT_LOADED'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      return;
    }

    if (this.selectedReportCategory === 'trend_analyse') {
      this.generateTrendReport();
    } else {
      this.generateReportWithCategory(this.selectedReportCategory);
    }
  }

  /**
   * Generate a trend analysis report (Auswertung Trend, Trend Matrix, or Periodenbericht).
   * Flow: fetch trend data -> generate charts -> send to API -> download PDF
   */
  private async generateTrendReport(): Promise<void> {
    if (!this.standort || !this.standortId) return;

    this.isGeneratingReport = true;

    // Open progress dialog
    const progressDialogRef = this.dialog.open(ProgressDialogComponent, {
      width: '450px',
      disableClose: true,
      data: {
        title: 'Trend-Analyse wird generiert',
        message: 'Daten werden geladen...',
        detailMessage: 'Schritt 1/3: Kontrolltermin-Daten werden analysiert.',
        showCancel: true,
        cancelling: false
      }
    });

    let cancelled = false;
    progressDialogRef.afterClosed().subscribe((result) => {
      if (result === 'cancel') {
        cancelled = true;
        this.isGeneratingReport = false;
        this.snackBar.open(this.translate.instant('STANDORT_EDIT.REPORT_CANCELLED'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      }
    });

    try {
      // Step 1: Fetch trend data from API
      console.log('Step 1: Fetching trend data...', this.trendDateFrom, this.trendDateTo);
      const trendData = await this.reportService.getTrendData(
        this.standortId, this.trendDateFrom, this.trendDateTo
      ).toPromise();

      if (cancelled) return;

      console.log('Trend data received:', trendData);

      // Step 2: Generate charts (unless it's trend_matrix which has no charts)
      let chartImages: { [key: string]: string } = {};

      if (this.trendReportType !== 'trend_matrix' && trendData.byCode) {
        if (progressDialogRef.componentInstance) {
          progressDialogRef.componentInstance.data.message = 'Diagramme werden erstellt...';
          progressDialogRef.componentInstance.data.detailMessage = 'Schritt 2/3: Grafiken werden generiert.';
        }

        console.log('Step 2: Generating charts...');
        chartImages = await this.trendChartService.generateAllCharts(
          trendData.byCode,
          trendData.summary.current_year,
          trendData.summary.previous_year,
          this.trendReportType
        );
        console.log('Charts generated:', Object.keys(chartImages).length);
      }

      if (cancelled) return;

      // Step 3: Send to API for PDF generation
      if (progressDialogRef.componentInstance) {
        progressDialogRef.componentInstance.data.message = 'PDF wird erstellt...';
        progressDialogRef.componentInstance.data.detailMessage = 'Schritt 3/3: Bericht wird als PDF generiert.';
      }

      console.log('Step 3: Generating PDF...');
      const pdfBlob = await this.reportService.generateStandortReport(
        this.standortId,
        'trend_analyse',
        {
          date_from: this.trendDateFrom,
          date_to: this.trendDateTo,
          report_type: this.trendReportType,
          chart_images: chartImages
        }
      ).toPromise();

      if (cancelled || !pdfBlob) return;

      // Close dialog
      progressDialogRef.close();
      this.isGeneratingReport = false;

      this.snackBar.open(this.translate.instant('STANDORT_EDIT.SUCCESS_TREND'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });

      console.log('PDF generated, size:', pdfBlob.size, 'bytes');

      // Download the PDF
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;

      const reportTypeNames: { [key: string]: string } = {
        'auswertung_trend': 'Auswertung-Trend',
        'trend_matrix': 'Trend-Matrix',
        'periodenbericht': 'Periodenbericht'
      };
      const typeName = reportTypeNames[this.trendReportType] || 'Trend-Analyse';
      const standortName = this.standort?.name || 'Standort';
      const sanitizedStandortName = standortName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const fromFormatted = this.formatDateForFilename(this.trendDateFrom);
      const toFormatted = this.formatDateForFilename(this.trendDateTo);
      link.download = `${sanitizedStandortName}-${typeName}-${fromFormatted}-${toFormatted}.pdf`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      progressDialogRef.close();
      this.isGeneratingReport = false;

      if (!cancelled) {
        console.error('Error generating trend report:', error);
        this.snackBar.open(this.translate.instant('STANDORT_EDIT.ERROR_TREND'), this.translate.instant('COMMON.CLOSE'), { duration: 5000 });
      }
    }
  }

  onTrendYearChange(): void {
    this.trendDateFrom = `${this.trendSelectedYear}-01-01`;
    this.trendDateTo = `${this.trendSelectedYear}-12-31`;
  }

  private formatDateForFilename(dateStr: string): string {
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}.${month}.${year}`;
  }

  /**
   * Preview trend report as HTML in a modal dialog.
   * Fetches data, generates charts, builds HTML preview, then shows dialog.
   * If user clicks "Als PDF speichern", triggers PDF generation.
   */
  async previewTrendReport(): Promise<void> {
    if (!this.standort || !this.standortId) {
      this.snackBar.open(this.translate.instant('STANDORT_EDIT.ERROR_NO_STANDORT_LOADED'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      return;
    }

    this.isGeneratingReport = true;

    const progressDialogRef = this.dialog.open(ProgressDialogComponent, {
      width: '450px',
      disableClose: true,
      data: {
        title: 'Vorschau wird erstellt',
        message: 'Daten werden geladen...',
        detailMessage: 'Kontrolltermin-Daten werden analysiert.',
        showCancel: true,
        cancelling: false
      }
    });

    let cancelled = false;
    progressDialogRef.afterClosed().subscribe((result) => {
      if (result === 'cancel') {
        cancelled = true;
        this.isGeneratingReport = false;
      }
    });

    try {
      // Step 1: Fetch trend data
      const trendData = await this.reportService.getTrendData(
        this.standortId, this.trendDateFrom, this.trendDateTo
      ).toPromise();

      if (cancelled) return;

      // Step 2: Generate charts
      let chartImages: { [key: string]: string } = {};
      if (this.trendReportType !== 'trend_matrix' && trendData.byCode) {
        if (progressDialogRef.componentInstance) {
          progressDialogRef.componentInstance.data.message = 'Diagramme werden erstellt...';
        }
        chartImages = await this.trendChartService.generateAllCharts(
          trendData.byCode,
          trendData.summary.current_year,
          trendData.summary.previous_year,
          this.trendReportType
        );
      }

      if (cancelled) return;

      // Step 3: Build HTML preview
      if (progressDialogRef.componentInstance) {
        progressDialogRef.componentInstance.data.message = 'Vorschau wird aufgebaut...';
      }

      const htmlContent = this.buildTrendPreviewHtml(trendData, chartImages);

      progressDialogRef.close();
      this.isGeneratingReport = false;

      // Step 4: Open preview dialog
      const reportTypeLabels: { [key: string]: string } = {
        'auswertung_trend': 'Auswertung Trend',
        'trend_matrix': 'Auswertung Trend: Matrix',
        'periodenbericht': 'Periodenbericht'
      };
      const title = `${reportTypeLabels[this.trendReportType] || 'Trendanalyse'}, ${this.formatDateForFilename(this.trendDateFrom)} - ${this.formatDateForFilename(this.trendDateTo)}`;

      const previewDialogRef = this.dialog.open(TrendPreviewDialogComponent, {
        width: '95vw',
        maxWidth: '95vw',
        height: '95vh',
        data: {
          title,
          htmlContent,
          chartImages,
          reportType: this.trendReportType,
          dateFrom: this.trendDateFrom,
          dateTo: this.trendDateTo
        }
      });

      previewDialogRef.afterClosed().subscribe(async (result) => {
        if (result === 'save_pdf') {
          // User wants to save as PDF - trigger the existing PDF generation flow
          this.generateTrendReport();
        }
      });

    } catch (error) {
      progressDialogRef.close();
      this.isGeneratingReport = false;
      if (!cancelled) {
        console.error('Error generating trend preview:', error);
        this.snackBar.open(this.translate.instant('STANDORT_EDIT.ERROR_PREVIEW'), this.translate.instant('COMMON.CLOSE'), { duration: 5000 });
      }
    }
  }

  /**
   * Build an HTML string for the trend report preview in the browser.
   * This renders charts as <img> with base64 src and data as tables.
   */
  private buildTrendPreviewHtml(trendData: any, chartImages: { [key: string]: string }): string {
    const dateFrom = this.formatDateForFilename(this.trendDateFrom);
    const dateTo = this.formatDateForFilename(this.trendDateTo);
    const currentYear = trendData.summary?.current_year || new Date().getFullYear();
    const previousYear = trendData.summary?.previous_year || currentYear - 1;
    const kundeName = trendData.kunde?.suchbegriff || trendData.kunde?.name || '';
    const standortName = trendData.standort?.name || this.standort?.name || '';
    const standortNummer = trendData.standort?.nummer || (this.standort as any)?.nummer || '';
    const standortStrasse = trendData.standort?.strasse || (this.standort as any)?.strasse || '';
    const standortPlz = trendData.standort?.plz || (this.standort as any)?.plz || '';
    const standortOrt = trendData.standort?.ort || (this.standort as any)?.ort || '';

    // Build standort address line
    let standortAddress = '';
    if (standortNummer) standortAddress += `${standortNummer}: `;
    standortAddress += standortName || 'N/A';
    if (standortStrasse) standortAddress += `, ${standortStrasse}`;
    if (standortPlz || standortOrt) standortAddress += `, ${standortPlz ? standortPlz + ' ' : ''}${standortOrt}`;

    const styles = `
      <style>
        * { box-sizing: border-box; margin: 0; padding: 0; font-family: Arial, sans-serif; }
        body { background: white; color: #333; font-size: 10pt; line-height: 1.4; padding: 30px 40px; }
        h1 { font-size: 14pt; font-weight: bold; color: #050505; margin-bottom: 8px; }
        h2 { font-size: 11pt; font-weight: bold; color: #050505; margin: 18px 0 8px 0; padding: 4px 6px; background: #e8e8e8; border-bottom: 2px solid #999; }
        .header { margin-bottom: 15px; padding-bottom: 10px; border-bottom: 1px solid #ddd; }
        .header-sub { font-size: 9pt; color: #555; line-height: 1.6; }
        .legend { margin: 6px 0 12px 0; font-size: 8pt; }
        .legend span { margin-right: 14px; }
        .dot { display: inline-block; width: 10px; height: 10px; border-radius: 50%; vertical-align: middle; margin-right: 3px; }
        .dot-green { background: #339900; }
        .dot-orange { background: #FF9900; }
        .dot-red { background: #FF0000; }
        .charts-grid { display: flex; flex-wrap: wrap; gap: 10px; margin: 10px 0; }
        .charts-grid img { max-width: 48%; height: auto; border: 1px solid #eee; }
        .chart-single img { max-width: 100%; max-height: 250px; height: auto; margin: 8px 0; }
        table { width: 100%; border-collapse: collapse; margin: 8px 0 16px 0; font-size: 8pt; }
        th, td { border: 1px solid #999; padding: 3px 5px; text-align: center; vertical-align: middle; }
        th { background: #e8e8e8; font-weight: bold; }
        td.left { text-align: left; }
        .status-dot { display: inline-block; width: 10px; height: 10px; border-radius: 50%; }
        .s-green { background: #339900; }
        .s-orange { background: #FF9900; }
        .s-red { background: #FF0000; }
        .summary-box { background: #f8f9fa; border: 1px solid #dee2e6; padding: 10px 14px; margin: 10px 0; border-radius: 3px; font-size: 9pt; }
        .summary-box strong { color: #333; }
        .page-break { page-break-before: always; margin-top: 30px; border-top: 2px solid #ccc; padding-top: 15px; }
        .no-data { padding: 20px; background: #fff3cd; border: 1px solid #ffc107; border-radius: 4px; color: #856404; margin-top: 20px; }
      </style>
    `;

    const byCode = trendData.byCode || {};
    const codes = Object.keys(byCode);

    // Build header HTML block (reused for empty and non-empty data)
    const headerHtml = `<div class="header"><h1>${this.getTrendReportTitle()}, ${dateFrom} - ${dateTo}</h1>`
      + `<div class="header-sub">`
      + (kundeName ? `<strong>Kunde:</strong> ${kundeName} &nbsp;|&nbsp; ` : '')
      + `<strong>Objekt:</strong> ${standortAddress} &nbsp;|&nbsp; `
      + `Auswahl: <span style="color:#339900">In Ordnung</span> + <span style="color:#FF9900">Hinweis</span> + <span style="color:#FF0000">Warnung</span>`
      + `</div></div>`;

    if (codes.length === 0) {
      return `<!DOCTYPE html><html><head>${styles}</head><body>
        ${headerHtml}
        <div class="no-data"><strong>Keine Daten vorhanden.</strong><br>Für den gewählten Zeitraum wurden keine abgeschlossenen Kontrolltermine gefunden.</div>
      </body></html>`;
    }

    let body = '';

    // Header
    body += headerHtml;

    // --- Report type specific rendering ---
    if (this.trendReportType === 'auswertung_trend') {
      // Auswertung Trend: per code, 2x2 grid (stacked bars left, detail charts right)
      for (const code of codes) {
        const cd = byCode[code];
        body += `<h2>${cd.label || code}</h2>`;

        // 2x2 chart grid
        const keyCStacked = `${code}_${currentYear}`;
        const keyCDetail = `${code}_${currentYear}_detail`;
        const keyPStacked = `${code}_${previousYear}`;
        const keyPDetail = `${code}_${previousYear}_detail`;

        body += `<table style="width:100%;border-collapse:collapse;border:none;margin:8px 0"><tbody>`;
        // Row 1: current year
        body += `<tr>`;
        body += `<td style="width:50%;padding:4px;border:none;vertical-align:top">`;
        if (chartImages[keyCStacked]) body += `<img src="${chartImages[keyCStacked]}" alt="${code} ${currentYear}" style="max-width:100%;height:auto">`;
        body += `</td>`;
        body += `<td style="width:50%;padding:4px;border:none;vertical-align:top">`;
        if (chartImages[keyCDetail]) body += `<img src="${chartImages[keyCDetail]}" alt="${code} ${currentYear} Detail" style="max-width:100%;height:auto">`;
        body += `</td>`;
        body += `</tr>`;
        // Row 2: previous year
        body += `<tr>`;
        body += `<td style="width:50%;padding:4px;border:none;vertical-align:top">`;
        if (chartImages[keyPStacked]) body += `<img src="${chartImages[keyPStacked]}" alt="${code} ${previousYear}" style="max-width:100%;height:auto">`;
        body += `</td>`;
        body += `<td style="width:50%;padding:4px;border:none;vertical-align:top">`;
        if (chartImages[keyPDetail]) body += `<img src="${chartImages[keyPDetail]}" alt="${code} ${previousYear} Detail" style="max-width:100%;height:auto">`;
        body += `</td>`;
        body += `</tr>`;
        body += `</tbody></table>`;

        // Summary line
        let totalG = 0, totalO = 0, totalR = 0, totalC = 0;
        const monthly = cd.monthly?.[currentYear] || {};
        for (let m = 1; m <= 12; m++) {
          const md = monthly[m] || { total: 0, green: 0, orange: 0, red: 0 };
          totalC += md.total; totalG += md.green; totalO += md.orange; totalR += md.red;
        }
        const systemTypesStr = cd.system_types?.length ? ` (${cd.system_types.join(', ')})` : '';
        body += `<div style="font-size:8pt;color:#555;margin-top:4px"><strong>${cd.label || code}</strong>${systemTypesStr} — ${cd.einheit || 'Stk'}: ${cd.indicators?.length || 0} Systeme, ${totalC} Kontrollen (${currentYear}): <span style="color:#339900">${totalG} In Ordnung</span>, <span style="color:#FF9900">${totalO} Hinweis</span>, <span style="color:#FF0000">${totalR} Warnung</span></div>`;
      }

    } else if (this.trendReportType === 'trend_matrix') {
      // Trend Matrix: organized by Objektstruktur (Gebäude > Sektion > Raum > Code > Indicators)
      const byObjektstruktur = trendData.byObjektstruktur || {};
      const hasPrevYear = (trendData.summary?.total_kontrolltermine_prev || 0) > 0;

      for (const gebName of Object.keys(byObjektstruktur).sort()) {
        const sektionen = byObjektstruktur[gebName];
        body += `<h2 style="font-size:11pt;margin:10px 0 4px 0;padding:3px 6px;background:#d0d0d0;border-bottom:2px solid #888">${gebName}</h2>`;

        for (const sekName of Object.keys(sektionen).sort()) {
          const rooms = sektionen[sekName];
          body += `<div style="font-size:10pt;font-weight:bold;margin:6px 0 3px 0;padding:2px 6px;background:#e8e8e8;border-bottom:1px solid #999">${sekName}</div>`;

          for (const roomName of Object.keys(rooms).sort()) {
            const codeGroups = rooms[roomName];
            body += `<div style="font-size:9pt;font-weight:bold;margin:4px 0 2px 4px">${roomName}</div>`;

            for (const codeKey of Object.keys(codeGroups).sort()) {
              const cg = codeGroups[codeKey];
              body += `<div style="font-size:7pt;font-weight:bold;color:#555;margin:3px 0 1px 8px">${cg.label || codeKey} (${cg.einheit || 'Stk'})</div>`;

              body += `<table style="margin-left:4px"><thead><tr><th style="text-align:left;width:100px">Falle</th><th style="width:35px">Nr.</th><th style="width:45px">Einheit</th>`;
              if (hasPrevYear) body += `<th style="width:35px">Jahr</th>`;
              for (let m = 1; m <= 12; m++) body += `<th>${m}</th>`;
              body += `</tr></thead><tbody>`;

              for (const ind of (cg.indicators || [])) {
                const rs = hasPrevYear ? ' rowspan="2"' : '';
                // Current year row
                body += `<tr><td class="left"${rs}>${ind.name || 'N/A'}</td><td${rs}>${ind.number || ''}</td><td${rs}>${ind.einheit || ''}</td>`;
                if (hasPrevYear) body += `<td><strong>${currentYear}</strong></td>`;
                for (let m = 1; m <= 12; m++) {
                  const visit = (ind.visits || []).find((v: any) => {
                    const vd = new Date(v.date);
                    return vd.getFullYear() === currentYear && (vd.getMonth() + 1) === m;
                  });
                  if (visit) {
                    const cls = visit.befall_status_id === 3 ? 's-red' : visit.befall_status_id === 2 ? 's-orange' : 's-green';
                    body += `<td><span class="status-dot ${cls}"></span></td>`;
                  } else {
                    body += `<td></td>`;
                  }
                }
                body += `</tr>`;
                // Previous year row — only when we have 2 years of data
                if (hasPrevYear) {
                  body += `<tr style="background:#f9f9f9"><td><strong>${previousYear}</strong></td>`;
                  for (let m = 1; m <= 12; m++) {
                    const visit = (ind.visits || []).find((v: any) => {
                      const vd = new Date(v.date);
                      return vd.getFullYear() === previousYear && (vd.getMonth() + 1) === m;
                    });
                    if (visit) {
                      const cls = visit.befall_status_id === 3 ? 's-red' : visit.befall_status_id === 2 ? 's-orange' : 's-green';
                      body += `<td><span class="status-dot ${cls}"></span></td>`;
                    } else {
                      body += `<td></td>`;
                    }
                  }
                  body += `</tr>`;
                }
              }
              body += `</tbody></table>`;
            }
          }
        }
      }

    } else if (this.trendReportType === 'periodenbericht') {
      // ======= SECTION 1: SYSTEMS SUMMARY =======
      const totalSystems = trendData.summary?.total_systems || codes.reduce((sum: number, c: string) => sum + (byCode[c].indicators?.length || 0), 0);
      const systemsSummary = trendData.systemsSummary || [];

      body += `<div style="margin: 10px 0 6px 0;">`;
      body += `<h2 style="font-size: 11pt; margin: 0 0 6px 0; padding: 0; background: none; border: none;">${totalSystems} Stk Monitoringsysteme am Ende der Periode (${dateTo}) installiert</h2>`;
      body += `<div style="margin-left: 10px; font-size: 9pt; line-height: 1.8;">`;
      if (systemsSummary.length > 0) {
        for (const sys of systemsSummary) {
          body += `<span style="display: inline-block; width: 48%; vertical-align: top;">${sys.count} Stk&nbsp;&nbsp;${sys.description}</span>`;
        }
      } else {
        // Fallback: build from byCode
        for (const code of codes) {
          const cd = byCode[code];
          const systemTypes = (cd.system_types || []).join(', ');
          const desc = (cd.label || code) + (systemTypes ? ': ' + systemTypes : '');
          body += `<span style="display: inline-block; width: 48%; vertical-align: top;">${cd.indicators?.length || 0} Stk&nbsp;&nbsp;${desc}</span>`;
        }
      }
      body += `</div></div>`;

      // ======= SECTION 2: BESUCHE SUMMARY =======
      const totalBesuche = trendData.summary?.total_kontrolltermine_current || 0;
      const besucheByType = trendData.besucheByType || {};
      body += `<div style="margin: 10px 0 6px 0;">`;
      body += `<h2 style="font-size: 11pt; margin: 0 0 6px 0; padding: 0; background: none; border: none;">${totalBesuche} Besuche in der Periode</h2>`;
      body += `<div style="margin-left: 10px; font-size: 9pt; line-height: 1.8;">`;
      for (const typ of Object.keys(besucheByType)) {
        body += `<div>${besucheByType[typ]}&nbsp;&nbsp;${typ}</div>`;
      }
      body += `</div></div>`;

      // Legend
      body += `<div class="legend"><span class="dot dot-green"></span> Kein Befall / In Ordnung <span class="dot dot-orange"></span> Befall &lt; SW (Hinweis) <span class="dot dot-red"></span> Befall &gt;= SW (Warnung)</div>`;

      // ======= SECTION 3: BESUCHE MATRIX TABLE =======
      const besucheMatrix = trendData.besucheMatrix || [];
      const matrixCodes: string[] = trendData.besucheMatrixCodes || codes;
      if (besucheMatrix.length > 0) {
        body += `<h2>Besuche in der Periode</h2>`;
        // Build matrix header with rotated code names
        body += `<table style="font-size: 8pt; width: 100%;"><thead><tr>`;
        body += `<th style="text-align:left; width: 65px;">Besuch</th>`;
        body += `<th style="text-align:left;">Einsatz</th>`;
        for (const code of matrixCodes) {
          body += `<th style="padding: 2px 3px; font-size: 7pt; white-space: nowrap;">${code}</th>`;
        }
        body += `<th style="padding: 2px 3px; font-size: 7pt; white-space: nowrap;">Maßnahmen</th>`;
        body += `</tr></thead><tbody>`;

        for (const row of besucheMatrix) {
          const rDate = row.datum ? new Date(row.datum) : null;
          const rDateStr = rDate ? `${String(rDate.getDate()).padStart(2,'0')}.${String(rDate.getMonth()+1).padStart(2,'0')}.${rDate.getFullYear()}` : '';
          body += `<tr><td class="left">${rDateStr}</td><td class="left" style="font-size: 7pt;">${row.einsatz || ''}</td>`;
          const codeStatuses = row.code_statuses || {};
          for (const code of matrixCodes) {
            const st = codeStatuses[code] || 0;
            let dotHtml = '';
            if (st === 3) dotHtml = `<span class="status-dot s-red"></span>`;
            else if (st === 2) dotHtml = `<span class="status-dot s-orange"></span>`;
            else if (st === 1) dotHtml = `<span class="status-dot s-green"></span>`;
            body += `<td>${dotHtml}</td>`;
          }
          // Massnahme diamond
          body += `<td>${row.has_massnahme ? '<span style="display:inline-block;width:8px;height:8px;transform:rotate(45deg);background:#0066CC;"></span>' : ''}</td>`;
          body += `</tr>`;
        }
        body += `</tbody></table>`;
      }

      // ======= SECTION 4: PER-CODE DETAIL =======
      for (const code of codes) {
        const cd = byCode[code];
        const systemTypesStr = (cd.system_types || []).length > 0 ? ` (${cd.system_types.join(', ')})` : '';
        body += `<h2>${cd.label || code}${systemTypesStr}</h2>`;
        // Chart
        const keyC = `${code}_${currentYear}`;
        if (chartImages[keyC]) {
          body += `<div class="chart-single"><img src="${chartImages[keyC]}" alt="${code} ${currentYear}"></div>`;
        }
        // Detail table
        body += `<table><thead><tr><th style="text-align:left">System</th><th>Nr.</th><th style="text-align:left">Standort</th><th>Besuch</th><th>Status</th><th>Befall</th><th>SW</th><th style="text-align:left">Schädling</th><th style="text-align:left">Maßnahme</th><th style="text-align:left">Artikel</th></tr></thead><tbody>`;
        for (const ind of (cd.indicators || [])) {
          for (const visit of (ind.visits || [])) {
            if (visit.date >= this.trendDateFrom && visit.date <= this.trendDateTo) {
              const cls = visit.befall_status_id === 3 ? 's-red' : visit.befall_status_id === 2 ? 's-orange' : 's-green';
              const visitDate = new Date(visit.date);
              const dateStr = `${String(visitDate.getDate()).padStart(2,'0')}.${String(visitDate.getMonth()+1).padStart(2,'0')}.${visitDate.getFullYear()}`;
              const massnahmeFormatted = this.formatJsonField(visit.massnahme);
              const artikelFormatted = this.formatJsonField(visit.artikel);
              body += `<tr><td class="left">${ind.name || 'N/A'}</td><td>${ind.number || ''}</td><td class="left">${ind.sektion || ''} / ${ind.room || ''}</td><td>${dateStr}</td><td><span class="status-dot ${cls}"></span></td><td>${visit.wert ?? '0'}</td><td>${ind.schwellenwert ?? '-'}</td><td class="left">${cd.label || code}</td><td class="left">${massnahmeFormatted}</td><td class="left">${artikelFormatted}</td></tr>`;
            }
          }
        }
        body += `</tbody></table>`;
      }

      // ======= SECTION 5: EINGESETZTE ARTIKEL =======
      const artikelSummary = trendData.artikelSummary || {};
      const artikelKeys = Object.keys(artikelSummary);
      if (artikelKeys.length > 0) {
        body += `<h2>Eingesetzte Artikel in Periode</h2>`;
        body += `<table style="width: 50%;"><thead><tr><th style="text-align:left">Artikel</th><th style="width: 60px; text-align: right;">Menge</th></tr></thead><tbody>`;
        for (const artName of artikelKeys) {
          body += `<tr><td class="left">${artName}</td><td style="text-align: right;">${artikelSummary[artName]}</td></tr>`;
        }
        body += `</tbody></table>`;
      }

      // ======= SECTION 6: MAßNAHMEN =======
      const massnahmenByRoom = trendData.massnahmenByRoom || {};
      const gebNames = Object.keys(massnahmenByRoom);
      if (gebNames.length > 0) {
        body += `<h2>Maßnahmen in Periode</h2>`;
        for (const gebName of gebNames) {
          body += `<div style="font-size: 10pt; font-weight: bold; margin: 10px 0 4px 0; padding: 3px 6px; background: #d0d0d0; border-bottom: 1px solid #888;">${gebName}</div>`;
          const sektionen = massnahmenByRoom[gebName] || {};
          for (const sekName of Object.keys(sektionen)) {
            body += `<div style="font-size: 9pt; font-weight: bold; margin: 6px 0 3px 8px; padding: 2px 4px; background: #e4e4e4;">${sekName}</div>`;
            const rooms = sektionen[sekName] || {};
            for (const roomName of Object.keys(rooms)) {
              body += `<div style="font-size: 8pt; font-weight: bold; margin: 4px 0 3px 16px; padding: 2px 4px; background: #f0f0f0;">${roomName}</div>`;
              const indicators: any[] = rooms[roomName] || [];
              body += `<table style="font-size: 7pt;"><thead><tr><th style="width:30px">Nr.</th><th style="text-align:left; width:90px">Code</th><th style="text-align:left; width:90px">System</th><th style="width:65px">Datum</th><th style="text-align:left">Maßnahme</th></tr></thead><tbody>`;
              for (const ind of indicators) {
                const visits: any[] = ind.visits || [];
                for (let vi = 0; vi < visits.length; vi++) {
                  const v = visits[vi];
                  const vDate = v.date ? new Date(v.date) : null;
                  const vDateStr = vDate ? `${String(vDate.getDate()).padStart(2,'0')}.${String(vDate.getMonth()+1).padStart(2,'0')}.${vDate.getFullYear()}` : '';
                  if (vi === 0) {
                    body += `<tr><td rowspan="${visits.length}">${ind.number || ''}</td><td class="left" rowspan="${visits.length}">${ind.code || ''}</td><td class="left" rowspan="${visits.length}">${ind.system_type || ''}</td><td>${vDateStr}</td><td class="left">${v.massnahme || ''}</td></tr>`;
                  } else {
                    body += `<tr><td>${vDateStr}</td><td class="left">${v.massnahme || ''}</td></tr>`;
                  }
                }
              }
              body += `</tbody></table>`;
            }
          }
        }
      }
    }

    return `<!DOCTYPE html><html><head><meta charset="UTF-8">${styles}</head><body>${body}</body></html>`;
  }

  private getTrendReportTitle(): string {
    const titles: { [key: string]: string } = {
      'auswertung_trend': 'Auswertung Trend',
      'trend_matrix': 'Auswertung Trend: Matrix',
      'periodenbericht': 'Periodenbericht'
    };
    return titles[this.trendReportType] || 'Trendanalyse';
  }

  /**
   * Format a field that might be a JSON string (array of objects) into readable text.
   * Handles massnahme and artikel fields from kontroll_indikator.
   */
  private formatJsonField(value: any): string {
    if (!value) return '';
    if (typeof value !== 'string') return String(value);

    // Check if it looks like JSON
    const trimmed = value.trim();
    if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          const parts = parsed.map((item: any) => {
            if (typeof item === 'object' && item !== null) {
              const name = item.name || item.beschreibung || '';
              const amount = item.amount || item.menge || '';
              if (name) {
                return amount ? `${name} (${Math.round(Number(amount))}x)` : name;
              }
              return '';
            }
            return String(item);
          }).filter((s: string) => s.length > 0);
          return parts.join(', ');
        }
      } catch (e) {
        // Not valid JSON, return as-is
      }
    }
    return value;
  }

  private generateReportWithCategory(reportCategory: ReportCategory): void {
    if (!this.standort || !this.standortId) {
      return;
    }

    this.isGeneratingReport = true;
    
    // Open progress dialog
    const progressDialogRef = this.dialog.open(ProgressDialogComponent, {
      width: '450px',
      disableClose: true, // Prevent closing by clicking outside
      data: {
        title: 'Bericht wird generiert',
        message: 'Bericht wird generiert',
        detailMessage: 'Das Erstellen des Berichts kann etwas Zeit in Anspruch nehmen - je nach Anzahl Indikatoren bis zu 1 Minute. Bitte um etwas Geduld.',
        showCancel: true,
        cancelling: false
      }
    });

    // Handle dialog close (cancel button)
    progressDialogRef.afterClosed().subscribe((result) => {
      if (result === 'cancel') {
        // User clicked cancel - unsubscribe from report generation
        if (this.reportGenerationSubscription) {
          this.reportGenerationSubscription.unsubscribe();
          this.reportGenerationSubscription = undefined;
        }
        this.isGeneratingReport = false;
        this.snackBar.open(this.translate.instant('STANDORT_EDIT.REPORT_CANCELLED'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      }
    });
    
    // Start report generation
    this.reportGenerationSubscription = this.reportService.generateStandortReport(this.standortId, reportCategory).subscribe({
      next: (pdfBlob: Blob) => {
        // Close dialog
        progressDialogRef.close();
        this.isGeneratingReport = false;
        this.reportGenerationSubscription = undefined;
        
        this.snackBar.open(this.translate.instant('STANDORT_EDIT.SUCCESS_REPORT'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
        
        console.log('Standort report generated, size:', pdfBlob.size, 'bytes');
        
        // Download the PDF directly
        const url = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        
        // Generate filename based on report category
        const categoryNames: { [key: string]: string } = {
          'objektstruktur': 'Objektstruktur',
          'systemplan': 'Systemplan',
          'qr_etiketten': 'QR-Etiketten',
          'indikatoren_liste': 'Indikatoren-Liste',
          'trend_analyse': 'Trend-Analyse'
        };
        const categoryName = categoryNames[reportCategory] || 'Report';
        const standortName = this.standort?.name || 'Standort';
        const sanitizedStandortName = standortName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
        link.download = `${sanitizedStandortName}-${categoryName}-${new Date().toISOString().split('T')[0]}.pdf`;
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      },
      error: (error) => {
        // Close dialog on error
        progressDialogRef.close();
        this.isGeneratingReport = false;
        this.reportGenerationSubscription = undefined;
        
        console.error('Error generating standort report:', error);
        
        // Check if error was due to cancellation
        if (error.name === 'AbortError' || error.message?.includes('aborted')) {
          // Already handled by dialog close handler
          return;
        }
        
        this.snackBar.open(this.translate.instant('STANDORT_EDIT.ERROR_REPORT'), this.translate.instant('COMMON.CLOSE'), { duration: 5000 });
      }
    });
  }

  private downloadGeneratedReport(reportUuid: string): void {
    // Download the generated report
    this.reportService.downloadReportByUuid(reportUuid).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `standort-report-${this.standortId}-${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      },
      error: (error) => {
        console.error('Error downloading report:', error);
        this.snackBar.open(this.translate.instant('STANDORT_EDIT.ERROR_DOWNLOAD'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      }
    });
  }

  private downloadReportByPath(reportPath: string): void {
    // Alternative download method using report path
    this.reportService.downloadReportByUuid(reportPath).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `standort-report-${this.standortId}-${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      },
      error: (error) => {
        console.error('Error downloading report by path:', error);
      }
    });
  }

  exportStandortAsPDF(): void {
    if (!this.standort || !this.standortId) {
      this.snackBar.open(this.translate.instant('STANDORT_EDIT.ERROR_NO_STANDORT_LOADED'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      return;
    }

    this.isExportingPDF = true;

    // Try to get plan image if available, otherwise export just indicators
    this.standortService.exportStandortAsPDF(this.standortId).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (blob: Blob) => {
        this.isExportingPDF = false;
        
        // Download PDF
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const standortName = this.standort?.name || 'Standort';
        const sanitizedStandortName = standortName
          .replace(/[^a-zA-Z0-9äöüÄÖÜß\s-]/g, '')
          .replace(/\s+/g, '-')
          .substring(0, 50);
        link.download = `standort-${sanitizedStandortName}-${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        this.snackBar.open(this.translate.instant('STANDORT_EDIT.SUCCESS_PDF'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      },
      error: (error) => {
        this.isExportingPDF = false;
        console.error('Error exporting standort as PDF:', error);
        this.snackBar.open(this.translate.instant('STANDORT_EDIT.ERROR_PDF_EXPORT'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      }
    });
  }

  /**
   * Navigate to client-edit page for the current kunde
   */
  navigateToClientEdit(): void {
    if (this.kundeId && this.kundeId !== 'new' && this.kundeId !== '0') {
      console.log('Navigating to client-edit with kundeId:', this.kundeId);
      this.router.navigate(['/client-edit', this.kundeId]);
    } else {
      console.warn('Cannot navigate to client-edit: invalid kundeId', this.kundeId);
      this.snackBar.open(this.translate.instant('STANDORT_EDIT.ERROR_NO_CLIENT'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
    }
  }

  
}

